import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios'; // Import axios


import './PatientDetail.css';
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine, ReferenceDot } from 'recharts';
import {
    AGTE_SECTIONS, AGTE_MONTH_BOUNDARIES, AGTE_CONVERSION,
    getDevelopmentalAge, calculateAGTEScores, getStandardScore, getOBI, getProbability
} from './testCalculations';

// AGTE üçün Vertikal Bar (Sütun)
const AGTE_Bar = ({ label, age, raw, color, isTotal = false }) => {
    // Maksimum 72 aya görə hündürlük hesablama
    const heightPercentage = Math.min((age / 72) * 100, 100);
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', minWidth: '50px', justifyContent: 'flex-end' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: color, marginBottom: '5px' }}>{age} ay</span>
            <div style={{ 
                width: isTotal ? '25px' : '15px', 
                height: `${heightPercentage}%`, 
                backgroundColor: color, 
                borderRadius: '4px 4px 0 0',
                transition: 'height 1s ease-in-out',
                boxShadow: 'inset -2px 0 5px rgba(0,0,0,0.1)'
            }}></div>
            <span style={{ fontSize: '0.6rem', color: '#64748b', marginTop: '8px', textAlign: 'center', height: '25px', lineHeight: '12px', fontWeight: 'bold' }}>{label}</span>
            <span style={{ fontSize: '0.7rem', color: '#334155', fontWeight: 'bold' }}>{raw} P</span>
        </div>
    );
};

// GOBDÖ üçün Info Box
const InfoBox = ({ label, raw, std }) => (
    <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#f0fdfa', borderLeft: '4px solid #1abc9c' }}>
        <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '4px' }}>{label}</span>
        <div style={{ fontWeight: '700', color: '#0f172a' }}>
            {raw ?? '-'} <small style={{fontWeight:'normal', color:'#64748b'}}>Ham</small> / 
            <span style={{color:'#1abc9c'}}> {std ?? '-'} Std</span>
        </div>
    </div>
);

// Məlumatları təhlükəsiz şəkildə mətnə çevirən köməkçi funksiya
const formatText = (data, fallback = 'Qeyd edilməyib') => {
    if (!data) return fallback;
    if (data && data.type === 'Buffer' && Array.isArray(data.data)) {
        try {
            return new TextDecoder().decode(new Uint8Array(data.data));
        } catch (e) {
            return fallback;
        }
    }
    return String(data);
};

const PatientDetail = ({ patient, currentUser, onClose, initialTestToOpen, initialTestToView }) => {
    const [activeTab, setActiveTab] = useState('inkisaf');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [videoUploading, setVideoUploading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [videos, setVideos] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0); // New state for upload progress
    const [uploadSpeed, setUploadSpeed] = useState(0); // Yükləmə sürəti
    const [timeLeft, setTimeLeft] = useState(0); // Qalan vaxt
    const [loadingVideos, setLoadingVideos] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const fileInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const [showTestForm, setShowTestForm] = useState(false);
const [testData, setTestData] = useState({ questions: [], options: [] });
const [answers, setAnswers] = useState({}); // {questionId: score}
const [testLoading, setTestLoading] = useState(false);
const [pastResults, setPastResults] = useState([]);
const [childSpeaks, setChildSpeaks] = useState(true);
// PatientDetail.js daxilində state-lərin yanına əlavə et:
const [allTestTypes, setAllTestTypes] = useState([]); // Bütün test növləri siyahısı
const [showSelectionList, setShowSelectionList] = useState(false); // Siyahını göstər/gizlə
const [openTabs, setOpenTabs] = useState({});
const [openResultId, setOpenResultId] = useState(null);
const [qeegImage, setQeegImage] = useState(null);
const [analyzing, setAnalyzing] = useState(false);
const [qeegResults, setQeegResults] = useState([]);
const [qeegResult, setQeegResult] = useState(null);
const [qeegLoading, setQeegLoading] = useState(false);
const [hasConsultation, setHasConsultation] = useState(false);
const [loading, setLoading] = useState(true);
const [consultationExists, setConsultationExists] = useState(true); // Əvvəlcə var kimi qəbul edək
const [isEditingConsultation, setIsEditingConsultation] = useState(false);
const [rawConsultationData, setRawConsultationData] = useState(null);
const [checking, setChecking] = useState(true);
const [edfFile, setEdfFile] = useState(null);
const [hardwareGain, setHardwareGain] = useState(4.0);

    const [reviews, setReviews] = useState([]);
    const [canAddReview, setCanAddReview] = useState(false);
    const [newReviewText, setNewReviewText] = useState("");
    const [loadingReviews, setLoadingReviews] = useState(false);
    const [isAddingReview, setIsAddingReview] = useState(false);
    const [editingReview, setEditingReview] = useState(null); // { id, text }

    const currentBranch = currentUser?.branches?.find(b => b.dbName === patient?.dbName);
    const teacherId = currentBranch?.teacherId;
    const teacherName = currentUser?.ad;

    const [viewingAnswers, setViewingAnswers] = useState(null); // Köhnə cavablar üçün state
    const [viewingTestResult, setViewingTestResult] = useState(null); // Yeni read-only və print görünüşü üçün state
    const [selectedExam, setSelectedExam] = useState(null);
    const [statusMsg, setStatusMsg] = useState(null); // { text: string, type: 'success' | 'error' }
    const [selectedDevTest, setSelectedDevTest] = useState(null);
    const [editingResultId, setEditingResultId] = useState(null);
    const [editingResultTeacherFin, setEditingResultTeacherFin] = useState(null);
    const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]);

    const handleViewDetails = React.useCallback(async (resultRecord) => {
        setTestLoading(true);
        try {
            const templateRes = await fetch(`/api/test-template/${resultRecord.test_id}`);
            const templateData = await templateRes.json();
            
            const answersRes = await axios.get(`/api/patient/test-answers/${resultRecord.id}`);
            const answersData = answersRes.data;
    
            const formAnswers = {};
            answersData.forEach(ans => {
                formAnswers[ans.question_id] = ans.score;
            });
            
            let detailsObj = {};
            try {
                  let detailsData = resultRecord.details;
            // Verilənlər bazasından gələn Buffer-i string-ə çeviririk
            if (detailsData && detailsData.type === 'Buffer' && Array.isArray(detailsData.data)) {
                detailsData = new TextDecoder().decode(new Uint8Array(detailsData.data));
            }
            detailsObj = typeof detailsData === 'string' ? JSON.parse(detailsData) : (detailsData || {});
        } catch(e) { console.error("Test detalları parse edilə bilmədi:", e); }
            
            setChildSpeaks(detailsObj.childSpeaks !== undefined ? detailsObj.childSpeaks : true);
    
            setTestData(templateData);
            setAnswers(formAnswers);
            setEditingResultId(resultRecord.id);
            setEditingResultTeacherFin(resultRecord.teacher_fin);
            setTestDate(new Date(resultRecord.created_at).toISOString().split('T')[0]);
            setShowTestForm(true);
        } catch (err) {
            console.error("Detay gətirmə xətası:", err);
            alert("Məlumatlar gətirilərkən xəta baş verdi.");
        } finally {
            setTestLoading(false);
        }
    }, []);

    const handleViewTestReadonly = React.useCallback(async (resultRecord) => {
        setTestLoading(true);
        try {
            const templateRes = await fetch(`/api/test-template/${resultRecord.test_id}`);
            const templateData = await templateRes.json();
            
            const answersRes = await axios.get(`/api/patient/test-answers/${resultRecord.id}`);
            setViewingTestResult({
                ...resultRecord,
                template: templateData,
                answers: answersRes.data
            });
        } catch (err) {
            console.error("Detay gətirmə xətası:", err);
            alert("Məlumatlar gətirilərkən xəta baş verdi.");
        } finally {
            setTestLoading(false);
        }
    }, []);

    useEffect(() => {
        if (initialTestToOpen) { setActiveTab('testler'); handleViewDetails(initialTestToOpen); }
    }, [initialTestToOpen, handleViewDetails]);

    useEffect(() => {
        if (initialTestToView) { setActiveTab('testler'); handleViewTestReadonly(initialTestToView); }
    }, [initialTestToView, handleViewTestReadonly]);
// 1. Məlumatları saxlamaq üçün əhatəli state
const [consultationData, setConsultationData] = useState({
    applicant_type: '', applicant_name: '', applicant_phone: '', address: '',
    hekim_info: { psixiatr: '', nevroloq: '', diaqnoz: '', dermanlar: '' },
    source_info: { source: '', sub_source: '', other: '' },
    konsultant_info: { ad_soyad: '', diaqnoz: '' },
    filial: '',
    anamnez: { hamilelik_gedisi: '', hamilelik_stresi: '', hamilelik_tipi: '', dogum: [], genetik: '', qan_ata: '', qan_ana: '', qan_usaq: '', xestelikler: '', nevroloji: [], saglamliq: '', yuxu: '' },
    motor_inkisaf: { oturma: '', hereket: '', ince_motor: '', gorme: '', esitme: '', esitme_testi: '' },
    nitq_loqopedik: { ilk_soz: '', seviyye: '', cumle: '', jest: '', fonematik: '', artikulyasiya: '', art_aparat: '', soz_ehtiyati: '', anlasilirlig: '' },
    oral_motor: { dil: [], uz: '', dodaqlar: '', damaq: '', cene_dislem: '', ceynem_udma: '', udma_refleksi: '', sovurma: '', trubcka: '', blender: '', agiz_suyu: '', nefes: '' },
    psixoloji_muayine: { emosional: '', diqqet: '', sosial: '', davranis: '', hereket_tekrari: '', muhite_reaksiya: '', dav_musahidesi: '' },
    duyusal_sensor: { hessasliq: '', temastan_qacir: '', vizual_stimul: '', isiq_hessasliq: '', beden_hereketleri: '' },
    oyun_sosial: { tek_oynama: '', repetitiv: '', toxunma: '', ses_reaksiya: '', qida_seciciliyi: '' },
    ozunequlluq: { gigiyena: '', qidalanma: '', geyinme: '', tualet: '' },
    mekteb_adaptasiya: { baslama_yasi: '', uygunlasma: '', unsiyyet: '', derse_diqqet: '', telimat: '', qaydalar: '', aqressiv: '' },
    sosial_muhit: { ictimai_yer: '', sese_reaksiya: '', geyime_reaksiya: '', qoxu_isiq: '' },
    denas_neuroport: { denas: { sag_yanaq: '', sol_yanaq: '', ust_dodaq: '', alt_dodaq: '', dilkoku: '', sag_el: '', sol_el: '', sag_ayaq: '', sol_ayaq: '', boyunarxasi: '' }, neuroport: '', psixoloji_rey: '' },
    multidisiplinar: {},
    proqram_aparatlar: { programs: {}, hours: {}, apps: {} },
    elave_qeyd: ''
});

// Nested (iç-içə) state-ləri asan yeniləmək üçün köməkçi funksiya
const handleNestedChange = (category, field, value) => {
    setConsultationData(prev => ({
        ...prev,
        [category]: { ...prev[category], [field]: value }
    }));
};

const handleRecalculateQeeg = () => {
    if (edfFile) {
        handleEdfToTxtAndDownload(edfFile, hardwareGain);
    } else {
        alert("Zəhmət olmasa əvvəlcə EDF faylı yükləyin.");
    }
};

const handleEdfToTxtAndDownload = (file, currentGain = hardwareGain) => {
    if (!file) return;
    setEdfFile(file);

    if (typeof setQeegLoading === 'function') setQeegLoading(true);

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = (e) => {
        try {
            const buffer = e.target.result;
            
            // Təhlükəsiz callback idarəçiliyi (ReferenceError-un qəti qarşısını almaq üçün):
            const safeSetPatientName = (name) => {
                if (typeof setPatientName === 'function') {
                    setPatientName(name);
                } else if (typeof setPatientData === 'function') {
                    // Əgər sənin state-in adı setPatientData-dırsa, obyekti qoruyub adı yeniləyirik:
                    setPatientData(prev => ({ ...prev, name: name }));
                } else {
                    console.log(`[EDF Sync Debug] Xəstə adı tapıldı: ${name}, lakin setPatientName state-i komponentdə tapılmadı.`);
                }
            };

            // Processor motorunu təhlükəsiz wrapper ilə çağırırıq
            processEdfFile(buffer, { 
                setPatientName: safeSetPatientName, 
                setQeegResult 
                }, { hardwareGain: currentGain });
            
            alert(`Dinamik EDF Analizi Tamamlandı!`);
        } catch (error) {
            console.error("Dinamik Analiz Hatası:", error);
            alert("Analiz işlənərkən xəta baş verdi.");
        } finally {
            if (typeof setQeegLoading === 'function') setQeegLoading(false);
        }
    };
};

// Checkbox-lar üçün köməkçi funksiya (Çoxlu seçim olanda array kimi saxlayır)
const handleCheckboxArrayChange = (category, field, value, isChecked) => {
    setConsultationData(prev => {
        const currentArray = prev[category][field] || [];
        if (isChecked) {
            return { ...prev, [category]: { ...prev[category], [field]: [...currentArray, value] } };
        } else {
            return { ...prev, [category]: { ...prev[category], [field]: currentArray.filter(item => item !== value) } };
        }
    });
};

const handleSourceChange = (val, checked) => {
    const sources = consultationData.sources || {};
    setConsultationData({
        ...consultationData,
        sources: { ...sources, [val]: checked }
    });
};

// 2. Düyməyə basanda işləyəcək əsas funksiya
const handleSaveConsultation = async () => {
    if (!consultationData.applicant_type) {
        alert("Zəhmət olmasa müraciət edən şəxsi qeyd edin!");
        return;
    }

    try {
        const res = await axios.post('/api/patient/save-consultation', {
            patientId: patient.id,
            dbName: patient.dbName,
            formData: consultationData,
            teacherFin: currentUser?.fin || '',
            patientFin: patient.fin || ''
        });

        if (res.data.success) {
            setIsEditingConsultation(false); // Formu oxuma rejiminə keçiririk
            setConsultationExists(true); // Artıq məlumatın olduğunu bildiririk
            setRawConsultationData(prev => ({...(prev || {}), teacher_fin: currentUser?.fin || ''}));
            alert("Konsultasiya uğurla qeyd edildi!");
        }
    } catch (err) {
        console.error("Xəta:", err);
        alert("Serverlə əlaqə qurulmadı. Backend kodunu yoxlayın.");
    }
};

// Pasiyent açılan kimi yoxla
useEffect(() => {
    const loadPatientData = async () => {
        try {
            const finParam = patient.fin ? `?patientFin=${encodeURIComponent(patient.fin)}` : '';
            const res = await axios.get(`/api/patient/consultation/${patient.dbName}/${patient.id}${finParam}`);
            const exists = res.data.exists;
            setConsultationExists(exists);
            setHasConsultation(exists);

            if (exists && res.data.data) {
                const dbData = res.data.data;
                setRawConsultationData(dbData);
                const parseJSON = (str) => {
                    try { return typeof str === 'string' ? JSON.parse(str) : (str || {}); } catch (e) { return {}; }
                };

                setConsultationData({
                    applicant_type: dbData.applicant_type || '',
                    applicant_name: dbData.applicant_name || formatText(patient.valideyn, ''),
                    applicant_phone: dbData.applicant_phone || formatText(patient.telefon, ''),
                    hekim_info: { psixiatr: '', nevroloq: '', diaqnoz: '', dermanlar: '', ...parseJSON(dbData.hekim_info) },
                    source_info: { source: '', sub_source: '', other: '', ...parseJSON(dbData.source_info) },
                    konsultant_info: { ad_soyad: '', diaqnoz: '', ...parseJSON(dbData.konsultant_info) },
                    filial: dbData.filial || '',
                    anamnez: { hamilelik_gedisi: '', hamilelik_stresi: '', hamilelik_tipi: '', dogum: [], genetik: '', qan_ata: '', qan_ana: '', qan_usaq: '', xestelikler: '', nevroloji: [], saglamliq: '', yuxu: '', ...parseJSON(dbData.anamnez) },
                    motor_inkisaf: { oturma: '', hereket: '', ince_motor: '', gorme: '', esitme: '', esitme_testi: '', ...parseJSON(dbData.motor_inkisaf) },
                    nitq_loqopedik: { ilk_soz: '', seviyye: '', cumle: '', jest: '', fonematik: '', artikulyasiya: '', art_aparat: '', soz_ehtiyati: '', anlasilirlig: '', ...parseJSON(dbData.nitq_loqopedik) },
                    oral_motor: { dil: [], uz: '', dodaqlar: '', damaq: '', cene_dislem: '', ceynem_udma: '', udma_refleksi: '', sovurma: '', trubcka: '', blender: '', agiz_suyu: '', nefes: '', ...parseJSON(dbData.oral_motor) },
                    psixoloji_muayine: { emosional: '', diqqet: '', sosial: '', davranis: '', hereket_tekrari: '', muhite_reaksiya: '', dav_musahidesi: '', ...parseJSON(dbData.psixoloji_muayine) },
                    duyusal_sensor: { hessasliq: '', temastan_qacir: '', vizual_stimul: '', isiq_hessasliq: '', beden_hereketleri: '', ...parseJSON(dbData.duyusal_sensor) },
                    oyun_sosial: { tek_oynama: '', repetitiv: '', toxunma: '', ses_reaksiya: '', qida_seciciliyi: '', ...parseJSON(dbData.oyun_sosial) },
                    ozunequlluq: { gigiyena: '', qidalanma: '', geyinme: '', tualet: '', ...parseJSON(dbData.ozunequlluq) },
                    mekteb_adaptasiya: { baslama_yasi: '', uygunlasma: '', unsiyyet: '', derse_diqqet: '', telimat: '', qaydalar: '', aqressiv: '', ...parseJSON(dbData.mekteb_adaptasiya) },
                    sosial_muhit: { ictimai_yer: '', sese_reaksiya: '', geyime_reaksiya: '', qoxu_isiq: '', ...parseJSON(dbData.sosial_muhit) },
                    denas_neuroport: { denas: { sag_yanaq: '', sol_yanaq: '', ust_dodaq: '', alt_dodaq: '', dilkoku: '', sag_el: '', sol_el: '', sag_ayaq: '', sol_ayaq: '', boyunarxasi: '' }, neuroport: '', psixoloji_rey: '', ...parseJSON(dbData.denas_neuroport) },
                    multidisiplinar: { ...parseJSON(dbData.multidisiplinar) },
                    proqram_aparatlar: { programs: {}, hours: {}, apps: {}, ...parseJSON(dbData.proqram_aparatlar) },
                    elave_qeyd: dbData.elave_qeyd || ''
                });
                setIsEditingConsultation(false);
            } else {
                setConsultationData(prev => ({
                    ...prev,
                    applicant_phone: formatText(patient.telefon, ''),
                    applicant_name: formatText(patient.valideyn, '')
                }));
                setIsEditingConsultation(true);
            }
        } catch (err) {
            console.error("Yoxlama xətası", err);
        } finally {
            setChecking(false);
            setLoading(false);
        }
    };

    if (patient) {
        loadPatientData();
        fetchPastResults();
        fetchExams();
    }
}, [patient]);

// Əgər konsultasiya yoxdursa, vərəqi göstər




const handleQeegUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('qeeg', file);
    formData.append('patientId', patient.id);
    formData.append('dbName', patient.dbName);
    formData.append('teacherFin', currentUser?.fin || '');
    formData.append('patientFin', patient.fin || '');

    setAnalyzing(true);
    setStatusMsg(null); // Yeni yükləmə başlayanda köhnə mesajı sil
    try {
        const response = await axios.post('/api/patient/analyze-qeeg', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
            setStatusMsg({ text: "Analiz uğurla tamamlandı!", type: 'success' });
            // Analiz bitdikdən sonra siyahını yeniləmək üçün:
            fetchExams(); 
            // 5 saniyə sonra mesajı avtomatik gizlət
            setTimeout(() => setStatusMsg(null), 5000);
        }
    } catch (err) {
        console.error("Analiz xətası:", err);
        setStatusMsg({ text: "Analiz zamanı xəta baş verdi.", type: 'error' });
    } finally {
        setAnalyzing(false);
    }
};

// Keçmiş cavabları gətirən funksiya
const fetchResultAnswers = async (resultId) => {
    try {
        const res = await axios.get(`/api/patient/test-answers/${resultId}`);
        setViewingAnswers(res.data);
    } catch (err) {
        console.error("Cavablar gətirilmədi", err);
        alert("Cavablar yüklənərkən xəta baş verdi.");
    }
};

// Keçmiş müayinələri gətirən funksiya
const fetchExams = async () => {
    try {
        const finParam = patient.fin ? `?patientFin=${encodeURIComponent(patient.fin)}` : '';
        const res = await axios.get(`/api/patient/exams/${patient.dbName}/${patient.id}${finParam}`);
        setQeegResults(res.data);
    } catch (err) {
        console.error("Müayinələr gətirilmədi", err);
    }
};

// Tab dəyişəndə datanı çək
useEffect(() => {
    if (activeTab === 'muayineler' && patient) {
        fetchExams();
    }
}, [activeTab, patient]);

const patientAgeMonths = React.useMemo(() => {
    if (!patient?.yas) return 0;
    const birthDate = new Date(patient.yas);
    const referenceDate = showTestForm ? new Date(testDate) : new Date();
    let months = (referenceDate.getFullYear() - birthDate.getFullYear()) * 12;
    months += referenceDate.getMonth() - birthDate.getMonth();
    return months <= 0 ? 0 : months;
}, [patient?.yas, testDate, showTestForm]);

    const fetchReviews = async () => {
        if (!patient) return; // Mütəxəssis ID-si olmasa belə rəylər mütləq gətirilməlidir
        setLoadingReviews(true);
        try {
            const finParam = patient.fin ? `&patientFin=${encodeURIComponent(patient.fin)}` : '';
            const res = await axios.get(`/api/patient/reviews/${patient.dbName}/${patient.id}?teacherFin=${currentUser?.fin || ''}${finParam}`);
            setReviews(res.data.reviews || []);
            setCanAddReview(!!res.data.canAdd);
        } catch (err) {
            console.error("Rəylər yüklənmədi:", err);
        } finally {
            setLoadingReviews(false);
        }
    };

    React.useEffect(() => {
        if (activeTab === 'rey' && patient) {
            fetchReviews();
        }
    }, [activeTab, patient, currentUser?.fin]);

    const handleAddReview = async () => {
        if (!newReviewText.trim()) return;
        try {
            const res = await axios.post('/api/patient/add-review', {
                patientId: patient.id,
                dbName: patient.dbName,
                teacherId: teacherId,
                teacherName: teacherName || 'Mütəxəssis',
                reviewText: newReviewText,
                teacherFin: currentUser?.fin || '',
                patientFin: patient.fin || ''
            });
            if (res.data.success) {
                setNewReviewText("");
                setIsAddingReview(false);
                fetchReviews();
                alert("Rəy uğurla əlavə edildi.");
            }
        } catch (err) {
            console.error("Rəy əlavə edilərkən xəta:", err);
            alert("Xəta baş verdi.");
        }
    };

    const handleEditReview = async () => {
        if (!editingReview) return;
        try {
            const res = await axios.post('/api/patient/edit-review', {
                reviewId: editingReview.id,
                reviewText: editingReview.text,
                teacherFin: currentUser.fin,
                level: currentUser?.level || 0
            });
            if (res.data.success) {
                alert("Rəy uğurla yeniləndi.");
                setEditingReview(null);
                fetchReviews();
            }
        } catch (err) {
            console.error("Rəy redaktə edilərkən xəta:", err);
            alert(err.response?.data?.message || "Xəta baş verdi.");
        }
    };
React.useEffect(() => {
    if (showTestForm && testData?.questions?.some(q => Number(q.section_id) >= 4)) {
        const initialTabs = {};
        AGTE_MONTH_BOUNDARIES.forEach((b, idx) => {
            const prevB = AGTE_MONTH_BOUNDARIES[idx - 1];
            const startAge = prevB ? parseInt(prevB.label.split('-')[1] || prevB.label.split(' ')[0]) : 0;
            const endAge = parseInt(b.label.split('-')[1] || b.label.split(' ')[0] || b.label.split('y')[0]);
            
            if (patientAgeMonths >= startAge && patientAgeMonths <= endAge) {
                initialTabs[b.label] = true;
            }
        });
        setOpenTabs(initialTabs);
    }
}, [patientAgeMonths, showTestForm, testData]);


const handleNewTestClick = async () => {
    setTestLoading(true);
    try {
        const res = await fetch('/api/test-definitions');
        if (res.ok) {
            const data = await res.json();
            setAllTestTypes(data);
            setShowSelectionList(true); // Seçim ekranını aç
        }
    } catch (err) {
        alert("Siyahı yüklənmədi.");
    } finally {
        setTestLoading(false);
    }
};

// Mövcud openTestForm funksiyasını ID qəbul edəcək şəkildə dəyişirik:



const fetchPastResults = async () => {
    try {
        const finParam = patient.fin ? `?patientFin=${encodeURIComponent(patient.fin)}` : '';
        const res = await fetch(`/api/patient/test-results/${patient.dbName}/${patient.id}${finParam}`);
        if (res.ok) {
            const data = await res.json();
            setPastResults(data);
        }
    } catch (err) {
        console.error("Test nəticələri gətirilərkən xəta:", err);
    }
};

// Testlər sekmesi açılan kimi nəticələri yenilə
React.useEffect(() => {
    if (activeTab === 'testler' && patient) {
        fetchPastResults();
    }
}, [activeTab, patient]);

// Test şablonunu (sualları) gətirən funksiya
const openTestForm = async (testId) => {
    setTestLoading(true);
    try {
        const res = await fetch(`/api/test-template/${testId}`);
        if (res.ok) {
            const data = await res.json();
            
            // Yeni test yaradılanda cavablar boş olur
            const initialAnswers = {};
            
            setTestData(data);
            setAnswers(initialAnswers);
            setChildSpeaks(true); // Standart olaraq uşağın danışdığı qəbul edilir
            setTestDate(new Date().toISOString().split('T')[0]);
            setShowSelectionList(false); // Siyahını bağla
            setEditingResultId(null);
            setEditingResultTeacherFin(null);
            setShowTestForm(true);      // Formu aç
        }
    } catch (err) {
        alert("Test yüklənərkən xəta.");
    } finally {
        setTestLoading(false);
    }
};






const renderAGTEForm = () => {
    const agteResults = calculateAGTEScores(testData, answers);

    return (
        <div className="tab-pane test-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#f4f7f6', padding: 0 }}>
            {/* Üst Sabit Panel: Başlıq və Düymələr */}
            <div className="test-form-header" style={{ background: '#fff', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div>
                    <h2 style={{ margin: 0, color: '#2c3e50', fontSize: '1.5rem' }}>Ankara Gelişim Tarama Envanteri (AGTE)</h2>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginTop: '5px' }}>
                        <small style={{ color: '#666' }}>Pasiyent Yaşı: <strong>{patientAgeMonths} ay</strong></small>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#666' }}>Test Tarixi:</label>
                            <input 
                                type="date" 
                                value={testDate} 
                                onChange={(e) => setTestDate(e.target.value)}
                                style={{ padding: '2px 5px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Orta Panel: Suallar (Accordion/Dropdown Sistemi) */}
            <div className="test-questions-wrapper" style={{ flexGrow: 1, overflowY: 'auto' }}>
                {[...AGTE_MONTH_BOUNDARIES].reverse().map((boundary, bIdx) => {
                    const isOpen = !!openTabs[boundary.label];
                    const originalIdx = AGTE_MONTH_BOUNDARIES.length - 1 - bIdx;
                    const prevBoundary = AGTE_MONTH_BOUNDARIES[originalIdx - 1];
                    const startIdx = prevBoundary ? prevBoundary.endIdx + 1 : 1;
                    
                    const monthQuestions = testData.questions.filter(
                        q => q.question_order >= startIdx && q.question_order <= boundary.endIdx
                    );

                    if (monthQuestions.length === 0) return null;

                    return (
                        <div key={boundary.label} style={{ marginBottom: '15px', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            {/* Dropdown Başlığı */}
                            <div 
                                onClick={() => setOpenTabs(prev => ({ ...prev, [boundary.label]: !isOpen }))}
                                style={{ 
                                    padding: '15px 20px', 
                                    background: isOpen ? '#e3f2fd' : '#fff', 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'background 0.3s'
                                }}
                            >
                                <span style={{ fontWeight: 'bold', color: '#34495e' }}>{boundary.label} SONUNA QƏDƏR</span>
                                <span style={{ fontSize: '12px', color: '#95a5a6' }}>{isOpen ? '▲ Qapat' : '▼ Aç'}</span>
                            </div>

                            {/* Sual Cədvəli */}
                            {isOpen && (
                                <div className="agte-table-container" style={{ padding: '0 15px 15px 15px' }}>
                                    <table className="agte-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                                        <thead>
                                            <tr style={{ fontSize: '12px', color: '#95a5a6', textAlign: 'center' }}>
                                                <th style={{ textAlign: 'left', padding: '10px', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>Sual Mətni</th>
                                                <th style={{ width: '45px', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>N.B</th>
                                                <th style={{ width: '45px', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>K.M</th>
                                                <th style={{ width: '45px', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>B.M</th>
                                                <th style={{ width: '45px', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>S.B</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {monthQuestions.map(q => {
                                                const sId = Number(q.section_id);
                                                const isChecked = answers[q.id] === 1;
                                                return (
                                                    <tr key={q.id} 
                                                        onClick={() => handleScoreSelect(q.id, isChecked ? 0 : 1)} 
                                                        style={{ borderBottom: '1px solid #f1f1f1', cursor: 'pointer' }}
                                                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fcfcfc'}
                                                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                    >
                                                        <td className="agte-question-cell" style={{ padding: '12px 10px', fontSize: '14px' , border: '1px solid black', color: '#2c3e50' }}>
                                                            <strong style={{ marginRight: '10px', color: '#bdc3c7' }}>{q.question_order}.</strong> 
                                                            {q.question_text}
                                                        </td>
                                                        {[4, 5, 6, 7].map(colId => (
                                                            <td key={colId} style={{ padding: 0, border: '1px solid black',  position: 'relative' }}>
                                                                {sId === colId && (
                                                                    <div className="checkmark-div" style={{
                                                                        backgroundColor: isChecked ? AGTE_SECTIONS[colId].color : AGTE_SECTIONS[colId].bg,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        color: isChecked ? '#fff' : 'transparent', 
                                                                        fontWeight: 'bold', fontSize: '18px',
                                                                        borderRadius: '4px',
                                                                        transition: 'all 0.2s',
                                                                        border: isChecked ? 'none' : `1px dashed ${AGTE_SECTIONS[colId].color}55`
                                                                    }}>
                                                                        ✔
                                                                    </div>
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                            {/* Ay Sonu Bazal/Hazır Xallar */}
                                            <tr style={{ backgroundColor: '#fdfdfd' }}>
                                                <td style={{ border: '1px solid #eee', textAlign: 'right', padding: '12px', fontWeight: 'bold', color: '#7f8c8d', fontSize: '13px' }}>BU AYIN HAZIR XALLARI (BAZAL)</td>
                                                {[4, 5, 6, 7].map(id => (
                                                    <td key={id} style={{ border: '1px solid #eee', textAlign: 'center', background: AGTE_SECTIONS[id].bg, fontWeight: 'bold', color: AGTE_SECTIONS[id].color }}>
                                                        {boundary.totals[id]}
                                                    </td>
                                                ))}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Yekun Alt Footer Panel (Sabit) */}
            <div className="test-form-footer">
                <div className="test-results-container">
                    <div className="test-main-score">
                        <span className="test-score-label">Yekun Bal:</span>
                        <span className="test-score-value">{agteResults.general}</span>
                    </div>
                    
                    <div className="test-section-scores">
                        {[4, 5, 6, 7].map(id => (
                            <div key={id} className="test-section-score-item">
                                <small>{AGTE_SECTIONS[id].short}:</small>
                                <strong style={{ color: AGTE_SECTIONS[id].color }}>{agteResults[id]}</strong>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="test-actions-container">
                    <button className="action-button cancel-button" onClick={() => { setShowTestForm(false); setAnswers({}); setEditingResultId(null); setEditingResultTeacherFin(null); }}>Ləğv Et</button>
                    {(!editingResultId || currentUser?.fin === editingResultTeacherFin || currentUser?.level > 0) && (
                        <button className="action-button" onClick={saveTest}>Yadda Saxla</button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Bal seçimi funksiyası
const handleScoreSelect = (qId, score) => {
    setAnswers(prev => {
        const newAnswers = { ...prev, [qId]: score };
        
        // Üst yaş qrupunu avtomatik açmaq məntiqi
        const currentQuestion = testData.questions.find(q => q.id === qId);
        const boundary = AGTE_MONTH_BOUNDARIES.find(b => b.endIdx === currentQuestion.question_order);
        
        if (boundary) {
            const prevBoundary = AGTE_MONTH_BOUNDARIES[AGTE_MONTH_BOUNDARIES.indexOf(boundary) - 1];
            const startIdx = prevBoundary ? prevBoundary.endIdx + 1 : 1;
            const blockQs = testData.questions.filter(q => q.question_order >= startIdx && q.question_order <= boundary.endIdx);
            
            const isFull = blockQs.every(q => newAnswers[q.id] === 1);
            if (isFull) {
                // Növbəti bloku tap və aç
                const nextBoundary = AGTE_MONTH_BOUNDARIES[AGTE_MONTH_BOUNDARIES.indexOf(boundary) + 1];
                if (nextBoundary) {
                    setOpenTabs(prevTabs => ({ ...prevTabs, [nextBoundary.label]: true }));
                }
            }
        }
        return newAnswers;
    });
};
const rawScores = { s1: 0, s2: 0, s3: 0 };
    testData.questions.forEach(q => {
        const val = answers[q.id] || 0;
        if (Number(q.section_id) === 1) rawScores.s1 += val;
        else if (Number(q.section_id) === 2 && childSpeaks) rawScores.s2 += val;
        else if (Number(q.section_id) === 3) rawScores.s3 += val;
    });

    const std1 = getStandardScore(rawScores.s1, 1);
    const std2 = childSpeaks ? getStandardScore(rawScores.s2, 2) : 0;
    const std3 = getStandardScore(rawScores.s3, 3);
    const sumStd = std1 + std2 + std3;

// Testi yadda saxla
const saveTest = async () => {
    // 1. Testin növünü müəyyən edirik (AGTE yoxsa GOBDÖ)
    const isAGTE = testData.questions.some(q => Number(q.section_id) >= 4);
    let payload = {};

    if (isAGTE) {
        // --- AGTE ÜÇÜN PAYLOAD HAZIRLIĞI ---
        const agteResults = calculateAGTEScores(testData, answers);
        
        // AGTE-də uşağın yaşını (ayını) hesablamaq və pasiyentin real yaşına görə məhdudlaşdırmaq
        let age4 = getDevelopmentalAge(agteResults[4], 4);
        age4 = Math.min(age4, patientAgeMonths);
        let age5 = getDevelopmentalAge(agteResults[5], 5);
        age5 = Math.min(age5, patientAgeMonths);
        let age6 = getDevelopmentalAge(agteResults[6], 6);
        age6 = Math.min(age6, patientAgeMonths);
        let age7 = getDevelopmentalAge(agteResults[7], 7);
        age7 = Math.min(age7, patientAgeMonths);
        let generalAge = getDevelopmentalAge(agteResults.general, 'genel');
        generalAge = Math.min(generalAge, patientAgeMonths);

        payload = {
            patientId: patient.id,
            dbName: patient.dbName,
            testId: 2, // Bazadakı AGTE ID-si
            testDate: testDate,
            teacherFin: currentUser?.fin || '',
            patientFin: patient.fin || '',
            answers,
            scores: {
                totalRaw: agteResults.general,
                totalStandard: generalAge, // AGTE-də standart bal yerinə ümumi inkişaf ayını saxlaya bilərik
                indexScore: 0,
                details: {
                    raw4: agteResults[4], raw5: agteResults[5], raw6: agteResults[6], raw7: agteResults[7],
                    age4, age5, age6, age7,
                    generalAge: generalAge
                }
            }
        };
    } // saveTest funksiyasının daxilindəki else hissəsi:
 else {
    const filteredQuestions = testData.questions.filter(q => childSpeaks || Number(q.section_id) !== 2);

    const currentRaw = { s1: 0, s2: 0, s3: 0 };
    testData.questions.forEach(q => {
        const val = answers[q.id] || 0;
        if (Number(q.section_id) === 1) currentRaw.s1 += val;
        else if (Number(q.section_id) === 2 && childSpeaks) currentRaw.s2 += val;
        else if (Number(q.section_id) === 3) currentRaw.s3 += val;
    });

    const std1 = getStandardScore(currentRaw.s1, 1);
    const std2 = childSpeaks ? getStandardScore(currentRaw.s2, 2) : 0;
    const std3 = getStandardScore(currentRaw.s3, 3);
    const sumStd = std1 + std2 + std3;
    const obi = getOBI(sumStd, childSpeaks);

    payload = {
        patientId: patient.id,
        dbName: patient.dbName,
        testId: 1,
        testDate: testDate,
        teacherFin: currentUser?.fin || '',
        patientFin: patient.fin || '',
        answers,
        scores: { 
            totalRaw: currentRaw.s1 + currentRaw.s2 + currentRaw.s3, 
            totalStandard: sumStd,
            indexScore: obi,
            details: { childSpeaks, std1, std2, std3, raw1: currentRaw.s1, raw2: currentRaw.s2, raw3: currentRaw.s3 }
        }
    };
}

    // --- SERVERƏ GÖNDƏRMƏ HİSSƏSİ (Hər iki test üçün ortaqdır) ---
    try {
        const url = editingResultId ? '/api/patient/update-test' : '/api/patient/save-test';
        if (editingResultId) payload.resultId = editingResultId;

        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (res.ok) {
            alert(editingResultId 
                ? "Test uğurla yeniləndi!" 
                : (isAGTE 
                    ? `AGTE uğurla qeyd edildi!\nÜmumi İnkişaf: ${payload.scores.details.generalAge} ay`
                    : `GOBDÖ uğurla qeyd edildi!\nOBİ İndeksi: ${payload.scores.indexScore}`)
            );
            
            setShowTestForm(false);
            setAnswers({});
            setEditingResultId(null);
            setEditingResultTeacherFin(null);
            if (typeof fetchPastResults === 'function') fetchPastResults();
        } else {
            const errorText = await res.text();
            alert("Xəta baş verdi: " + errorText);
        }
    } catch (err) {
        console.error("Save Error:", err);
        alert("Xəta baş verdi! İnternet bağlantısını yoxlayın.");
    }
};;
    // isEditing və ya patient dəyişdikdə form məlumatlarını yenilə
   // PatientDetail.js daxilindəki useEffect-i belə yenilə:
React.useEffect(() => {
    if (patient) {
        setFormData({
            ad: formatText(patient.ad, ''),
            yas: patient.yas ? new Date(patient.yas).toISOString().split('T')[0] : '',
            telefon: formatText(patient.telefon, ''),
            unvan: formatText(patient.unvan, ''),
            valideyn: formatText(patient.valideyn, ''),
            seriya: formatText(patient.seriya, ''),
            fin: formatText(patient.fin, ''),
            endirim: patient.endirim || 0,
            balans: formatText(patient.balans, ''),
            info: patient.info || ''
        });
    }
    if (!isEditing) {
        setPhotoFile(null);
        setPhotoPreview(null);
    }
}, [patient, isEditing]);

    const fetchVideos = async () => {
        setLoadingVideos(true);
        try {
            const finParam = patient.fin ? `?patientFin=${encodeURIComponent(patient.fin)}` : '';
            const res = await fetch(`/api/patient/videos/${patient.dbName}/${patient.id}${finParam}`);
            if (res.ok) {
                const data = await res.json();
                setVideos(data);
            }
        } catch (err) {
            console.error("Videoları yükləmək mümkün olmadı:", err);
        } finally {
            setLoadingVideos(false);
        }
    };

    React.useEffect(() => {
        if (activeTab === 'videolar' && patient) {
            fetchVideos();
        }
    }, [activeTab, patient]);

    const getEmbedLink = (url) => {
        if (!url) return '';
        if (url.includes('drive.google.com')) {
            return url.replace(/\/view\?.*$/, '/preview');
        }
        return url;
    };

    if (!patient) return null;



    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhotoChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handlePhotoClick = () => {
        if (isEditing && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleVideoChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setVideoFile(e.target.files[0]);
        }
    };

    const uploadVideo = async () => {
        if (!videoFile) return;

        const userId = patient.id;
        const dbName = patient.dbName;

        console.log("Video Drive-a yüklənir:", videoFile.name);
        setVideoUploading(true);
        setUploadProgress(0); // Reset progress at the start
        setUploadSpeed(0);
        setTimeLeft(0);
        const startTime = Date.now();

        const videoUploadData = new FormData();
        
        if (!userId || !dbName) {
            alert("Xəstə ID və ya Veritabanı adı tapılmadı. Video yüklənmədi.");
            setVideoUploading(false);
            return;
        }

        videoUploadData.append('video', videoFile);
        videoUploadData.append('patientFin', patient.fin || '');
        try {
            // Axios istifadə edərək real progress-i tuturuq
            const response = await axios.post(`/api/patient/upload-video/${dbName}/${userId}`, videoUploadData, {
                onUploadProgress: (event) => {
                    if (event.total) {
                        const percent = Math.round((event.loaded * 100) / event.total);
                        setUploadProgress(percent);

                        const timeElapsed = (Date.now() - startTime) / 1000; // saniyə
                        if (timeElapsed > 0) {
                            const speed = event.loaded / timeElapsed; // bytes/sec
                            setUploadSpeed((speed / (1024 * 1024)).toFixed(2));
                            const remaining = (event.total - event.loaded) / speed;
                            setTimeLeft(Math.round(remaining));
                        }
                    }
                }
            });

            if (response.status === 200) {
                console.log("Video uğurla yükləndi:", response.data.videoLink);
                alert('Video uğurla yükləndi!');
                
                // Sıfırlama
                setVideoFile(null);
                if (videoInputRef.current) {
                    videoInputRef.current.value = "";
                }
                setShowVideoModal(false);
                fetchVideos();
            }
        } catch (err) {
            console.error("Video yükləmə xətası:", err);
            alert(`Video yüklənərkən xəta baş verdi: ${err.response?.data?.message || err.message}`);
            setUploadProgress(0); // Reset progress on error
        } finally {
            setVideoUploading(false);
        }
    };

    const handleSave = async () => {
        // TODO: Məlumatların (formData) yadda saxlanması üçün API zəngi burada olmalıdır.
        console.log("Məlumatlar yadda saxlanılır (simulyasiya):", { ...formData });

        const userId = patient.id;
        const dbName = patient.dbName;

        if (photoFile) {
            console.log("Şəkil yüklənir:", photoFile.name);
            const photoUploadData = new FormData();
            

            if (!userId || !dbName) {
                alert("Xəstə ID və ya Veritabanı adı tapılmadı. Şəkil yüklənmədi.");
                setIsEditing(false); // Redaktə rejimindən çıx
                return;
            }

            photoUploadData.append('photo', photoFile);

            try {
                const res = await fetch(`/api/patient/upload-photo/${dbName}/${userId}`, {
                    method: 'POST',
                    body: photoUploadData,
                });
                if (!res.ok) {
                    const errorData = await res.json().catch(() => ({ message: 'Şəkil yüklənmədi' }));
                    throw new Error(errorData.message);
                }
                await res.json();
            } catch (err) {
                console.error("Şəkil yükləmə xətası:", err);
                alert(`Şəkil yüklənərkən xəta baş verdi: ${err.message}`);
            }
        }

        alert('Məlumatlar yadda saxlandı (simulyasiya).');
        setIsEditing(false);
        onClose(); // Pəncərəni bağlayırıq və Dashboard-un yenilənməsini təmin edirik.
    };

    const ReadOnlyRow = ({ label, value, isArray = false }) => {
        let displayValue = value;
        if (isArray && Array.isArray(value)) {
            displayValue = value.join(', ');
        }
        if (!displayValue || (Array.isArray(displayValue) && displayValue.length === 0)) {
            displayValue = <em style={{ color: '#94a3b8' }}>(boş)</em>;
        }
        return (
            <tr>
                <td className="label-cell" style={{ fontWeight: '600', color: '#475569' }}>{label}:</td>
                <td className="input-cell" style={{ background: '#f8fafc', color: '#1e293b' }}>{displayValue}</td>
            </tr>
        );
    };

    const renderConsultationView = () => (
        <div className="consultation-table-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Dəyərləndirmə Vərəqi (Baxış)</h3>
                {(!rawConsultationData?.teacher_fin || String(rawConsultationData.teacher_fin).trim().toLowerCase() === String(currentUser?.fin || '').trim().toLowerCase() || currentUser?.level > 0) && (
                    <button className="action-button" onClick={() => setIsEditingConsultation(true)}>Redaktə Et</button>
                )}
            </div>
            <table className="doc-table">
                <tbody>
                    <tr><th colSpan="2" className="table-header">ÜMUMİ MƏLUMAT</th></tr>
                    <ReadOnlyRow label="Müraciət edən" value={consultationData.applicant_type} />
                    <ReadOnlyRow label="Əlaqə nömrəsi" value={consultationData.applicant_phone} />
                    <ReadOnlyRow label="Yaşayış ünvanı" value={consultationData.address} />
                    
                    <tr><th colSpan="2" className="table-subheader">Həkim Məlumatları</th></tr>
                    <ReadOnlyRow label="Psixiatr" value={consultationData.hekim_info.psixiatr} />
                    <ReadOnlyRow label="Nevroloq" value={consultationData.hekim_info.nevroloq} />
                    <ReadOnlyRow label="Diaqnoz" value={consultationData.hekim_info.diaqnoz} />
                    <ReadOnlyRow label="Dərmanlar" value={consultationData.hekim_info.dermanlar} />

                    <tr><th colSpan="2" className="table-subheader">Anamnez</th></tr>
                    <ReadOnlyRow label="Hamiləlik gedişi" value={`${consultationData.anamnez.hamilelik_gedisi} ${consultationData.anamnez.hamilelik_stresi ? `(${consultationData.anamnez.hamilelik_stresi})` : ''}`} />
                    <ReadOnlyRow label="Hamiləlik tipi" value={consultationData.anamnez.hamilelik_tipi} />
                    <ReadOnlyRow label="Doğum" value={consultationData.anamnez.dogum} isArray />
                    <ReadOnlyRow label="Genetik faktor" value={consultationData.anamnez.genetik} />
                    <ReadOnlyRow label="Qan qrupu (Ata/Ana/Uşaq)" value={`${consultationData.anamnez.qan_ata || '-'} / ${consultationData.anamnez.qan_ana || '-'} / ${consultationData.anamnez.qan_usaq || '-'}`} />
                    <ReadOnlyRow label="Keçirilmiş xəstəliklər" value={consultationData.anamnez.xestelikler} />
                    <ReadOnlyRow label="Nevroloji anamnez" value={consultationData.anamnez.nevroloji} isArray />
                    <ReadOnlyRow label="Sağlamlıq vəziyyəti" value={consultationData.anamnez.saglamliq} />
                    <ReadOnlyRow label="Yuxu" value={consultationData.anamnez.yuxu} />

                    <tr><th colSpan="2" className="table-subheader">Motor İnkişaf</th></tr>
                    <ReadOnlyRow label="Oturma" value={consultationData.motor_inkisaf.oturma} />
                    <ReadOnlyRow label="Hərəkətlər" value={consultationData.motor_inkisaf.hereket} />
                    <ReadOnlyRow label="İncə motorika" value={consultationData.motor_inkisaf.ince_motor} />
                    <ReadOnlyRow label="Görmə" value={consultationData.motor_inkisaf.gorme} />
                    <ReadOnlyRow label="Eşitmə" value={consultationData.motor_inkisaf.esitme} />
                    <ReadOnlyRow label="Eşitmə testi" value={consultationData.motor_inkisaf.esitme_testi} />

                    <tr><th colSpan="2" className="table-subheader">Nitq / Loqopedik Müayinə</th></tr>
                    <ReadOnlyRow label="İlk söz" value={consultationData.nitq_loqopedik.ilk_soz} />
                    <ReadOnlyRow label="Nitq səviyyəsi" value={consultationData.nitq_loqopedik.seviyye} />
                    <ReadOnlyRow label="Cümlə qurma" value={consultationData.nitq_loqopedik.cumle} />
                    <ReadOnlyRow label="Artikulyasiya" value={consultationData.nitq_loqopedik.artikulyasiya} />
                    <ReadOnlyRow label="Söz ehtiyatı" value={consultationData.nitq_loqopedik.soz_ehtiyati} />
                    <ReadOnlyRow label="Nitqin anlaşılırlığı" value={consultationData.nitq_loqopedik.anlasilirlig} />

                    <tr><th colSpan="2" className="table-subheader">Psixoloji Müayinə</th></tr>
                    <ReadOnlyRow label="Emosional vəziyyət" value={consultationData.psixoloji_muayine.emosional} />
                    <ReadOnlyRow label="Diqqət və yaddaş" value={consultationData.psixoloji_muayine.diqqet} />
                    <ReadOnlyRow label="Sosial inkişaf" value={consultationData.psixoloji_muayine.sosial} />
                    <ReadOnlyRow label="Davranış müşahidələri" value={consultationData.psixoloji_muayine.dav_musahidesi} />

                    <tr><th colSpan="2" className="table-subheader">Yekun</th></tr>
                    <ReadOnlyRow label="Psixoloji rəy" value={consultationData.denas_neuroport.psixoloji_rey} />
                    <ReadOnlyRow label="Əlavə Qeyd" value={consultationData.elave_qeyd} />
                </tbody>
            </table>
        </div>
    );

    const renderConsultationForm = () => (
           <div className="consultation-table-container" style={{ paddingBottom: '50px' }}>
        <table className="doc-table">
            <thead>
                <tr>
                    <th colSpan="2" className="table-header">İLKİN DİAQNOSTİK DƏYƏRLƏNDİRMƏ PROTOKOLU</th>
                </tr>
            </thead>
            <tbody>
                {/* 1-4. MÜRACİƏT EDƏN ŞƏXS VƏ XƏSTƏ İNFO */}
                <tr>
                    <td className="label-cell">1. Müraciət edən şəxs:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Ata', 'Ana', 'Baba', 'Nənə', 'Digər'].map(v => (
                                <label key={v}><input type="radio" name="applicant" value={v} checked={consultationData.applicant_type === v} onChange={(e) => setConsultationData({...consultationData, applicant_type: e.target.value})} /> {v}</label>
                            ))}
                        </div>
                        {consultationData.applicant_type && (
                            <input type="text" className="table-input" placeholder="Müraciət edənin adı" style={{marginTop: '10px'}} value={consultationData.applicant_name} onChange={(e) => setConsultationData({...consultationData, applicant_name: e.target.value})} />
                        )}
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">2. Əlaqə nömrəsi:</td>
                    <td className="input-cell"><input type="text" className="table-input" value={consultationData.applicant_phone} onChange={(e) => setConsultationData({...consultationData, applicant_phone: e.target.value})} /></td>
                </tr>
                <tr>
                    <td className="label-cell">3. Pasiyentin adı, soyadı, d.tarixi:</td>
                    <td className="input-cell"><input type="text" className="table-input no-border" value={`${formatText(patient.ad)} / ${patient.yas ? new Date(patient.yas).toLocaleDateString('az-AZ') : ''}`} readOnly /></td>
                </tr>
                <tr>
                    <td className="label-cell">4. Yaşayış ünvanı:</td>
                    <td className="input-cell"><input type="text" className="table-input" value={consultationData.address} onChange={(e) => setConsultationData({...consultationData, address: e.target.value})} /></td>
                </tr>
                
                {/* 5. HƏKİM İNFO */}
                <tr>
                    <td colSpan="2" className="table-subheader" style={{backgroundColor: '#e9ecef'}}>5. Həkim Məlumatları</td>
                </tr>
                <tr>
                    <td className="label-cell">Psixiatr:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('hekim_info', 'psixiatr', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Nevroloq:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('hekim_info', 'nevroloq', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Diaqnoz:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('hekim_info', 'diaqnoz', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Qəbul edilən dərmanlar:</td>
                    <td className="input-cell"><textarea className="table-textarea" onChange={e => handleNestedChange('hekim_info', 'dermanlar', e.target.value)}></textarea></td>
                </tr>
                {/* 6. MƏRKƏZ HAQQINDA İNFORMASİYA */}
                <tr>
                    <td colSpan="2" className="table-subheader" style={{backgroundColor: '#e9ecef'}}>6. Mərkəz haqqında informasiyanı haradan əldə etmişiniz?</td>
                </tr>
                <tr>
                    <td className="label-cell">Mənbə:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal" style={{ flexWrap: 'wrap', gap: '15px' }}>
                            {['Həkim', 'İnstagram', 'Tiktok', 'Facebook', 'TV verliş', 'Digər'].map(v => (
                                <label key={v}>
                                    <input 
                                        type="radio" 
                                        name="source_main" 
                                        value={v} 
                                        onChange={(e) => {
                                            handleNestedChange('source_info', 'source', e.target.value);
                                            handleNestedChange('source_info', 'sub_source', ''); // Əsas mənbə dəyişəndə alt mənbəni sıfırla
                                        }} 
                                    /> {v}
                                </label>
                            ))}
                        </div>

                        {/* Alt Seçimlər (Dinamik açılır) */}
                        {['İnstagram', 'Tiktok', 'Facebook'].includes(consultationData.source_info.source) && (
                            <div style={{ marginTop: '10px', padding: '10px', borderLeft: '3px solid #1abc9c', backgroundColor: '#f9fcfb' }}>
                                <div className="radio-group-horizontal">
                                    <label><input type="radio" name="sub_source" value="Esmira Qafarova şəxsi səhifəsi" onChange={e => handleNestedChange('source_info', 'sub_source', e.target.value)} /> Esmira Qafarova şəxsi səhifəsi</label>
                                    <label><input type="radio" name="sub_source" value="QNPM səhifəsi" onChange={e => handleNestedChange('source_info', 'sub_source', e.target.value)} /> QNPM səhifəsi</label>
                                </div>
                            </div>
                        )}

                        {consultationData.source_info.source === 'TV verliş' && (
                            <div style={{ marginTop: '10px', padding: '10px', borderLeft: '3px solid #1abc9c', backgroundColor: '#f9fcfb' }}>
                                <div className="radio-group-horizontal">
                                    <label><input type="radio" name="sub_source" value="Təsir dairəsi" onChange={e => handleNestedChange('source_info', 'sub_source', e.target.value)} /> Təsir dairəsi</label>
                                    <label><input type="radio" name="sub_source" value="Sağlam Günay" onChange={e => handleNestedChange('source_info', 'sub_source', e.target.value)} /> Sağlam Günay</label>
                                    <label><input type="radio" name="sub_source" value="Öz aramızda" onChange={e => handleNestedChange('source_info', 'sub_source', e.target.value)} /> Öz aramızda</label>
                                </div>
                            </div>
                        )}

                        {consultationData.source_info.source === 'Digər' && (
                            <div style={{ marginTop: '10px' }}>
                                <input type="text" className="table-input" placeholder="Zəhmət olmasa qeyd edin..." onChange={e => handleNestedChange('source_info', 'other', e.target.value)} />
                            </div>
                        )}
                    </td>
                </tr>

                {/* 7-9. KONSULTANT VƏ FİLİAL */}
                <tr>
                    <td colSpan="2" className="table-subheader" style={{backgroundColor: '#e9ecef'}}>7-9. Konsultant və Filial</td>
                </tr>
                <tr>
                    <td className="label-cell">Konsultantın ad/soyadı:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('konsultant_info', 'ad_soyad', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Konsultantın diaqnozu:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('konsultant_info', 'diaqnoz', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Terapiya alacaq filial:</td>
                    <td className="input-cell">
                        <select className="table-input" onChange={(e) => setConsultationData({...consultationData, filial: e.target.value})}>
                            <option value="">Seçin...</option>
                            {['Gənclik', 'Nərimanov', 'Yasamal', 'Zabrat', '9 mkr', 'Vorovski', 'Xırdalan', 'Binə', 'Sumqayıt', 'Naxçıvan', 'Ağcabədi', 'Xankəndi'].map(f => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </select>
                    </td>
                </tr>

                {/* ANAMNEZ BÖLMƏSİ */}
                <tr>
                    <th colSpan="2" className="table-subheader">DƏYƏRLƏNDİRMƏ MƏLUMATLARI - ANAMNEZ</th>
                </tr>
                <tr>
                    <td className="label-cell">Hamiləliyin gedişi:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            <label><input type="radio" name="h_gedisi" value="Normal" onChange={e => { handleNestedChange('anamnez', 'hamilelik_gedisi', e.target.value); handleNestedChange('anamnez', 'hamilelik_stresi', ''); }} /> Normal</label>
                            <label><input type="radio" name="h_gedisi" value="Problemli" onChange={e => handleNestedChange('anamnez', 'hamilelik_gedisi', e.target.value)} /> Problemli</label>
                            {consultationData.anamnez.hamilelik_gedisi === 'Problemli' && (
                                <div style={{display:'inline-flex', gap:'10px', marginLeft: '15px', borderLeft: '2px solid #ccc', paddingLeft: '10px'}}>
                                    <label><input type="radio" name="h_prob" value="Stresli" onChange={e => handleNestedChange('anamnez', 'hamilelik_stresi', e.target.value)}/> Stresli</label>
                                    <label><input type="radio" name="h_prob" value="Stressiz" onChange={e => handleNestedChange('anamnez', 'hamilelik_stresi', e.target.value)}/> Stressiz</label>
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Hamiləlik tipi:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Təbii', 'Süni'].map(v => (
                                <label key={v}><input type="radio" name="h_tipi" value={v} onChange={e => handleNestedChange('anamnez', 'hamilelik_tipi', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Doğum haqqında:</td>
                    <td className="input-cell">
                         <div className="checkbox-group-grid">
                            {['Vaxtında', 'Vaxtından əvvəl', 'Çətin', 'Təbii doğuş', 'Keysəriyyə'].map(v => (
                                <label key={v}><input type="checkbox" onChange={e => handleCheckboxArrayChange('anamnez', 'dogum', v, e.target.checked)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Genetik faktor (qohum evliliyi):</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Vardır', 'Yoxdur'].map(v => (
                                <label key={v}><input type="radio" name="genetik_f" value={v} onChange={e => handleNestedChange('anamnez', 'genetik', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Qan qrupu:</td>
                    <td className="input-cell">
                        <div style={{display:'flex', gap:'15px', flexWrap:'wrap'}}>
                            <label style={{fontSize: '0.85rem', color: '#555'}}>Ata: <input type="text" className="table-input" style={{width:'80px', display:'inline-block'}} onChange={e=>handleNestedChange('anamnez','qan_ata',e.target.value)}/></label>
                            <label style={{fontSize: '0.85rem', color: '#555'}}>Ana: <input type="text" className="table-input" style={{width:'80px', display:'inline-block'}} onChange={e=>handleNestedChange('anamnez','qan_ana',e.target.value)}/></label>
                            <label style={{fontSize: '0.85rem', color: '#555'}}>Uşaq: <input type="text" className="table-input" style={{width:'80px', display:'inline-block'}} onChange={e=>handleNestedChange('anamnez','qan_usaq',e.target.value)}/></label>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Keçirilmiş ciddi xəstəliklər:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('anamnez', 'xestelikler', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Nevroloji anamnez:</td>
                    <td className="input-cell">
                        <div className="checkbox-group-grid">
                            {['Epileptik tutmalar', 'Baş ağrıları', 'Gicəllənmə'].map(v => (
                                <label key={v}><input type="checkbox" onChange={e => handleCheckboxArrayChange('anamnez', 'nevroloji', v, e.target.checked)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Sağlamlıq vəziyyəti:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('anamnez', 'saglamliq', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Yuxu:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('anamnez', 'yuxu', e.target.value)} /></td>
                </tr>

                {/* BURADAN AŞAĞI DİGƏR BÖLMƏLƏR (Motor, Nitq və s.) GƏLƏCƏK */}
{/* MOTOR İNKİŞAF BÖLMƏSİ */}
                <tr>
                    <th colSpan="2" className="table-subheader">MOTOR İNKİŞAF</th>
                </tr>
                <tr>
                    <td className="label-cell">Oturma:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('motor_inkisaf', 'oturma', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Əl-qol hərəkətləri, yerimə, qaçma:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('motor_inkisaf', 'hereket', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">İncə / kiçik motorika:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('motor_inkisaf', 'ince_motor', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Görmə vəziyyəti:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Zəif', 'Eynək istifadə edir'].map(v => (
                                <label key={v}><input type="radio" name="gorme" value={v} onChange={e => handleNestedChange('motor_inkisaf', 'gorme', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Eşitmə vəziyyəti:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Zəif', 'Aparat istifadə edir'].map(v => (
                                <label key={v}><input type="radio" name="esitme" value={v} onChange={e => handleNestedChange('motor_inkisaf', 'esitme', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Eşitmə testi:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('motor_inkisaf', 'esitme_testi', e.target.value)} /></td>
                </tr>

                {/* NİTQ / LOQOPEDİK MÜAYİNƏ BÖLMƏSİ */}
                <tr>
                    <th colSpan="2" className="table-subheader">NİTQ / LOQOPEDİK MÜAYİNƏ</th>
                </tr>
                <tr>
                    <td className="label-cell">İlk söz yaşı:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('nitq_loqopedik', 'ilk_soz', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Nitq səviyyəsi:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Tam', 'Qismən', 'Danışmır'].map(v => (
                                <label key={v}><input type="radio" name="nitq_sev" value={v} onChange={e => handleNestedChange('nitq_loqopedik', 'seviyye', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Cümlə qurma:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('nitq_loqopedik', 'cumle', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Jest və təqlid:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('nitq_loqopedik', 'jest', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Fonematik qavrama:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('nitq_loqopedik', 'fonematik', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Artikulyasiya:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Çətinlik var'].map(v => (
                                <label key={v}><input type="radio" name="artik" value={v} onChange={e => handleNestedChange('nitq_loqopedik', 'artikulyasiya', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Artikulyasiya aparatının vəziyyəti:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('nitq_loqopedik', 'art_aparat', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Söz ehtiyatı:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Yaşına uyğun', 'Məhduddur'].map(v => (
                                <label key={v}><input type="radio" name="soz_ehtiyati" value={v} onChange={e => handleNestedChange('nitq_loqopedik', 'soz_ehtiyati', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Nitqin anlaşılırlığı:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Aydın', 'Qeyri-aydın', 'Təkrar edir'].map(v => (
                                <label key={v}><input type="radio" name="anlasilirlig" value={v} onChange={e => handleNestedChange('nitq_loqopedik', 'anlasilirlig', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>

                {/* ORAL-MOTOR / AĞIZ APARATI BÖLMƏSİ */}
                <tr>
                    <th colSpan="2" className="table-subheader">ORAL-MOTOR / AĞIZ APARATI</th>
                </tr>
                <tr>
                    <td className="label-cell">Dil:</td>
                    <td className="input-cell">
                        <div className="checkbox-group-grid">
                            {['İri', 'Kiçik', 'Dar', 'Qısa', 'Dilaltı qısa', 'Asimmetrik'].map(v => (
                                <label key={v}><input type="checkbox" onChange={e => handleCheckboxArrayChange('oral_motor', 'dil', v, e.target.checked)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Üz:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('oral_motor', 'uz', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Dodaqlar:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('oral_motor', 'dodaqlar', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Damaq:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('oral_motor', 'damaq', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Çənə və dişləmənin vəziyyəti:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Progeniya', 'Prognatiya', 'Ön açıq dişləm'].map(v => (
                                <label key={v}><input type="radio" name="cene_dislem" value={v} onChange={e => handleNestedChange('oral_motor', 'cene_dislem', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Çeynəmə və udma:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('oral_motor', 'ceynem_udma', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Udma refleksi:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Var', 'Yox'].map(v => (
                                <label key={v}><input type="radio" name="udma_ref" value={v} onChange={e => handleNestedChange('oral_motor', 'udma_refleksi', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Sovurma:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Var', 'Yox'].map(v => (
                                <label key={v}><input type="radio" name="sovurma_ref" value={v} onChange={e => handleNestedChange('oral_motor', 'sovurma', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Trubçka ilə su içmə:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Var', 'Yox'].map(v => (
                                <label key={v}><input type="radio" name="trubcka_ref" value={v} onChange={e => handleNestedChange('oral_motor', 'trubcka', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Blenderdən yemək yeyibmi / neçə yaşına qədər:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('oral_motor', 'blender', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Ağız suyu:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Var', 'Yox'].map(v => (
                                <label key={v}><input type="radio" name="agiz_suyu_ref" value={v} onChange={e => handleNestedChange('oral_motor', 'agiz_suyu', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Nəfəs:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('oral_motor', 'nefes', e.target.value)} /></td>
                </tr>

                {/* PSİXOLOJİ MÜAYİNƏ BÖLMƏSİ */}
                <tr>
                    <th colSpan="2" className="table-subheader">PSİXOLOJİ MÜAYİNƏ</th>
                </tr>
                <tr>
                    <td className="label-cell">Emosional vəziyyət:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Sakit', 'Həyəcanlı', 'Aqressiv', 'Çəkingən'].map(v => (
                                <label key={v}><input type="radio" name="emosional" value={v} onChange={e => handleNestedChange('psixoloji_muayine', 'emosional', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Diqqət və yaddaş:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Zəif', 'Qısa müddətli'].map(v => (
                                <label key={v}><input type="radio" name="diqqet" value={v} onChange={e => handleNestedChange('psixoloji_muayine', 'diqqet', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Sosial inkişaf:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Çətinlik var'].map(v => (
                                <label key={v}><input type="radio" name="sosial_inkisaf" value={v} onChange={e => handleNestedChange('psixoloji_muayine', 'sosial', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Davranış və oyun:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('psixoloji_muayine', 'davranis', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Hərəkət təkrarı:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('psixoloji_muayine', 'hereket_tekrari', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Yeni mühitə reaksiya:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('psixoloji_muayine', 'muhite_reaksiya', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Davranış müşahidələri:</td>
                    <td className="input-cell"><textarea className="table-textarea" onChange={e => handleNestedChange('psixoloji_muayine', 'dav_musahidesi', e.target.value)}></textarea></td>
                </tr>

                {/* DUYUSAL / SENSOR MÜAYİNƏ BÖLMƏSİ */}
                <tr>
                    <th colSpan="2" className="table-subheader">DUYUSAL / SENSOR MÜAYİNƏ</th>
                </tr>
                <tr>
                    <td className="label-cell">Həssaslıq (su, qum və s.):</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Bəli', 'Xeyr'].map(v => (
                                <label key={v}><input type="radio" name="hessasliq" value={v} onChange={e => handleNestedChange('duyusal_sensor', 'hessasliq', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Fiziki təmasdan qaçır:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Bəli', 'Xeyr'].map(v => (
                                <label key={v}><input type="radio" name="temasdan_qacir" value={v} onChange={e => handleNestedChange('duyusal_sensor', 'temastan_qacir', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Vizual stimullara həddindən artıq maraq:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Bəli', 'Xeyr'].map(v => (
                                <label key={v}><input type="radio" name="vizual_marag" value={v} onChange={e => handleNestedChange('duyusal_sensor', 'vizual_stimul', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Parlaq işığa həssaslıq:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Bəli', 'Xeyr'].map(v => (
                                <label key={v}><input type="radio" name="isiq_hessasliq" value={v} onChange={e => handleNestedChange('duyusal_sensor', 'isiq_hessasliq', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Bədən hərəkətləri:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Çox aktiv', 'Passiv'].map(v => (
                                <label key={v}><input type="radio" name="beden_hereketleri" value={v} onChange={e => handleNestedChange('duyusal_sensor', 'beden_hereketleri', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>

                {/* OYUN VƏ SOSİAL FƏALİYYƏT BÖLMƏSİ */}
                <tr>
                    <th colSpan="2" className="table-subheader">OYUN VƏ SOSİAL FƏALİYYƏT</th>
                </tr>
                <tr>
                    <td className="label-cell">Tək oynamağa üstünlük verir:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Bəli', 'Xeyr'].map(v => (
                                <label key={v}><input type="radio" name="tek_oynama" value={v} onChange={e => handleNestedChange('oyun_sosial', 'tek_oynama', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Repetitiv (təkrarlanan) davranışlar:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Bəli', 'Xeyr'].map(v => (
                                <label key={v}><input type="radio" name="repetitiv" value={v} onChange={e => handleNestedChange('oyun_sosial', 'repetitiv', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Toxunma reaksiyası:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Həssas', 'Reaksiya vermir'].map(v => (
                                <label key={v}><input type="radio" name="toxunma_reaksiya" value={v} onChange={e => handleNestedChange('oyun_sosial', 'toxunma', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Səs reaksiyası:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Həddindən artıq', 'Reaksiya vermir'].map(v => (
                                <label key={v}><input type="radio" name="ses_reaksiya" value={v} onChange={e => handleNestedChange('oyun_sosial', 'ses_reaksiya', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Qida seçiciliyi:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Var', 'Yox'].map(v => (
                                <label key={v}><input type="radio" name="qida_seciciliyi" value={v} onChange={e => handleNestedChange('oyun_sosial', 'qida_seciciliyi', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>

                {/* HƏRƏKƏT KOORDİNASİYASI / ÖZÜNƏQULLUQ */}
                <tr>
                    <th colSpan="2" className="table-subheader">HƏRƏKƏT KOORDİNASİYASI / ÖZÜNƏQULLUQ</th>
                </tr>
                <tr>
                    <td className="label-cell">Gigiyena:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Normal', 'Zəif'].map(v => (
                                <label key={v}><input type="radio" name="gigiyena" value={v} onChange={e => handleNestedChange('ozunequlluq', 'gigiyena', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">Qidalanma:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('ozunequlluq', 'qidalanma', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Geyinmə:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('ozunequlluq', 'geyinme', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Tualet vərdişləri:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('ozunequlluq', 'tualet', e.target.value)} /></td>
                </tr>

                {/* MƏKTƏB VƏ SOSİAL ADAPTASİYA */}
                <tr>
                    <th colSpan="2" className="table-subheader">MƏKTƏB VƏ SOSİAL ADAPTASİYA</th>
                </tr>
                <tr>
                    <td className="label-cell">Bağçaya / məktəbə başlama yaşı:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('mekteb_adaptasiya', 'baslama_yasi', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Uyğunlaşma:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('mekteb_adaptasiya', 'uygunlasma', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Ünsiyyət çətinliyi:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('mekteb_adaptasiya', 'unsiyyet', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Dərsə diqqət:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('mekteb_adaptasiya', 'derse_diqqet', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Təlimatları anlama:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('mekteb_adaptasiya', 'telimat', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Məktəb qaydalarına riayət:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('mekteb_adaptasiya', 'qaydalar', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Aqressiv davranış:</td>
                    <td className="input-cell">
                        <div className="radio-group-horizontal">
                            {['Var', 'Yox'].map(v => (
                                <label key={v}><input type="radio" name="aqressiv_dav" value={v} onChange={e => handleNestedChange('mekteb_adaptasiya', 'aqressiv', e.target.value)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>

                {/* SOSİAL MÜHİTDƏ DAVRANIŞ */}
                <tr>
                    <th colSpan="2" className="table-subheader">SOSİAL MÜHİTDƏ DAVRANIŞ</th>
                </tr>
                <tr>
                    <td className="label-cell">İctimai yerlərdə davranış:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('sosial_muhit', 'ictimai_yer', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Səsə reaksiya:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('sosial_muhit', 'sese_reaksiya', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Geyimə reaksiya:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('sosial_muhit', 'geyime_reaksiya', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Qoxu / işığa həssaslıq:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('sosial_muhit', 'qoxu_isiq', e.target.value)} /></td>
                </tr>

                {/* DENAS, NEUROPORT VƏ PSİXOLOJİ RƏY */}
                <tr>
                    <th colSpan="2" className="table-subheader">ÖLÇÜMLƏR VƏ RƏYLƏR</th>
                </tr>
                <tr>
                    <td className="label-cell">DENAS Göstəriciləri:</td>
                    <td className="input-cell">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                            {['sağ yanaq', 'sol yanaq', 'üst dodaq', 'alt dodaq', 'dilkökü', 'sağ əl', 'sol əl', 'sağ ayaq', 'sol ayaq', 'boyunarxası'].map(part => {
                                const stateKey = part.replace(' ', '_').replace('ə', 'e').replace('ğ', 'g').replace('ş', 's').replace('ü', 'u').replace('ö', 'o').replace('ı', 'i');
                                return (
                                    <label key={part} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <span style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{part}:</span>
                                        <input type="text" className="table-input" style={{ padding: '2px 5px' }} onChange={e => {
                                            setConsultationData(prev => ({ ...prev, denas_neuroport: { ...prev.denas_neuroport, denas: { ...prev.denas_neuroport.denas, [stateKey]: e.target.value } } }));
                                        }} />
                                    </label>
                                );
                            })}
                        </div>
                    </td>
                </tr>
                <tr>
                    <td className="label-cell">NEUROPORT:</td>
                    <td className="input-cell"><input type="text" className="table-input" onChange={e => handleNestedChange('denas_neuroport', 'neuroport', e.target.value)} /></td>
                </tr>
                <tr>
                    <td className="label-cell">Psixoloji rəy:</td>
                    <td className="input-cell"><textarea className="table-textarea" rows="4" onChange={e => handleNestedChange('denas_neuroport', 'psixoloji_rey', e.target.value)}></textarea></td>
                </tr>

                {/* MULTİDİSİPLİNAR YANAŞMADA YÖNLƏNDİRMƏ */}
                <tr>
                    <th colSpan="2" className="table-subheader">MULTİDİSİPLİNAR YANAŞMADA YÖNLƏNDİRMƏ</th>
                </tr>
                <tr>
                    <td colSpan="2" className="input-cell">
                        <div className="checkbox-group-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', padding: '10px' }}>
                            {['Genetik test', 'Eşitmə testi', 'Qurd parazit testi', 'Burun boğaz yoxlanışı', 'Ortodont', 'Pediatr', 'Nevroloq', 'Psixiatr', 'Osteopat', 'Mutrisioloq – dietoloq', 'At terapiyası (hipoterapiya)', 'Üzgüçülük', 'Art terapiya', 'Musiqi terapiyası'].map(v => (
                                <label key={v}><input type="checkbox" onChange={e => handleCheckboxArrayChange('multidisiplinar', 'secimler', v, e.target.checked)} /> {v}</label>
                            ))}
                        </div>
                    </td>
                </tr>

                {/* PASİYENTİN PROQRAMI VƏ APARATLAR */}
                <tr>
                    <th colSpan="2" className="table-subheader">PASİYENTİN PROQRAMI VƏ APARATLAR</th>
                </tr>
                <tr>
                    <td colSpan="2" style={{ padding: 0 }}>
                        <div className="programs-apps-container" style={{ display: 'flex', width: '100%', flexWrap: 'wrap' }}>
                            <div className="programs-section" style={{ flex: '1 1 50%', minWidth: '250px', boxSizing: 'border-box' }}>
                                <h4 style={{ margin: '10px 15px', color: '#2c3e50', fontSize: '0.9rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '5px' }}>Pasiyentin proqramı (Həftəlik dərs saatı)</h4>
                                <div style={{ padding: '10px 15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {["Psixoloq", "Psixopedaqoq", "Loqoped – defektoloq", "Xüsusi kəkələmə proqramı", "Duyu bütünləmə", "İnklüziv idman", "İnklüziv təhsil", "Loqo-masaj", "Bədən masajı", "Refleksoloq"].map(prog => (
                                        <div key={prog} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input type="checkbox" checked={!!consultationData.proqram_aparatlar.programs?.[prog]} onChange={e => {
                                                    const p = consultationData.proqram_aparatlar.programs || {};
                                                    handleNestedChange('proqram_aparatlar', 'programs', {...p, [prog]: e.target.checked});
                                                }} /> {prog}
                                            </label>
                                            {consultationData.proqram_aparatlar.programs?.[prog] && (
                                                <div style={{ paddingLeft: '25px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Həftədə:</span>
                                                    <input 
                                                        type="number" 
                                                        min="1"
                                                        className="table-input" 
                                                        style={{ width: '60px', padding: '2px 5px', fontSize: '0.85rem' }}
                                                        value={consultationData.proqram_aparatlar.hours?.[prog] || ''}
                                                        onChange={e => {
                                                            const h = consultationData.proqram_aparatlar.hours || {};
                                                            handleNestedChange('proqram_aparatlar', 'hours', {...h, [prog]: e.target.value});
                                                        }} 
                                                    />
                                                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>dəfə</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="apps-section" style={{ flex: '1 1 50%', minWidth: '250px', boxSizing: 'border-box' }}>
                                <h4 style={{ margin: '10px 15px', color: '#2c3e50', fontSize: '0.9rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '5px' }}>Aparatlar</h4>
                                <div style={{ padding: '10px 15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {["TDCS", "Mikropolyarizasiya", "Neyroport", "Denas", "Forbrain", "Neurofeedback", "Biofeedback", "Eliton", "Soundsory", "Vibromasaj aparatı", "Beurer diafraqma aparatı", "Elektrofarez aparatı", "Tomatis"].map(app => (
                                        <div key={app} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input type="checkbox" checked={!!consultationData.proqram_aparatlar.apps?.[app]} onChange={e => {
                                                    const a = consultationData.proqram_aparatlar.apps || {};
                                                    handleNestedChange('proqram_aparatlar', 'apps', {...a, [app]: e.target.checked});
                                                }} /> {app}
                                            </label>
                                            
                                            {/* ƏGƏR TDCS SEÇİLİBSƏ, ALT MENYU AÇILIR */}
                                            {app === 'TDCS' && consultationData.proqram_aparatlar.apps?.['TDCS'] && (
                                                <select 
                                                    className="table-input" 
                                                    style={{ marginTop: '5px', backgroundColor: '#f0fdf4', border: '1px solid #16a34a' }}
                                                    onChange={e => handleNestedChange('proqram_aparatlar', 'tdcs_type', e.target.value)}
                                                >
                                                    <option value="">Seçin...</option>
                                                    {["Davranış", "Anlama qavrama", "Diqqət", "Həyəcan təşviş"].map(opt => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>

                {/* ƏLAVƏ QEYD */}
                <tr>
                    <td className="label-cell">Əlavə Qeyd:</td>
                    <td className="input-cell">
                        <textarea className="table-textarea" rows="4" onChange={(e) => setConsultationData({...consultationData, elave_qeyd: e.target.value})}></textarea>
                    </td>
                </tr>

         
            </tbody>
        </table>
        
        <button className="confirm-btn" style={{marginTop: '20px', width: '100%'}} onClick={handleSaveConsultation}>MƏLUMATLARI TƏSDİQLƏ VƏ YADDA SAXLA</button>
    </div>
    );

    const renderTabContent = () => {


   
        switch (activeTab) {
           case 'deyer':
                if (checking) return <p>Yoxlanılır...</p>;
                // The existing form is now inside a function, we need to call it
                // But we also need the view mode.
                if (isEditingConsultation) {
                    // This is a simplified call. The actual implementation will be a diff.
                    return renderConsultationForm();
                } else {
                    return renderConsultationView();
                }
    
          
            case 'muayineler':
    return (
        <div className="tab-pane">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                <h2 style={{ margin: 0, color: '#2c3e50' }}>Müayinə Tarixçəsi (QEEG)</h2>
                <div>
                    <input 
                        type="file" 
                        id="qeeg-upload" 
                        style={{ display: 'none' }} 
                        accept="image/*" 
                        onChange={handleQeegUpload} 
                    />
                    <label htmlFor="qeeg-upload" className="action-button" style={{ cursor: 'pointer', padding: '10px 20px' }}>
                        {analyzing ? 'Analiz edilir...' : '+ Yeni Müayinə Əlavə Et'}
                    </label>
                </div>
            </div>

            {/* Əgər müayinə varsa, Müqayisə Düyməsi */}
            {qeegResults.length >= 2 && (
                <button 
                    className="action-button" 
                    onClick={handleCompareExams}
                    style={{ backgroundColor: '#8e44ad', marginBottom: '20px', width: '100%' }}
                >
                    ✨ Süni İntellekt ilə İnkişaf Müqayisəsini Hazırla
                </button>
            )}

            <div className="exams-list">
                {qeegResults.length > 0 ? qeegResults.map((exam) => (
                    <div key={exam.id} style={{ border: '1px solid #e0e6ed', borderRadius: '12px', marginBottom: '20px', backgroundColor: '#fff', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e0e6ed', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 'bold' }}>Tarix: {new Date(exam.created_at).toLocaleDateString('az-AZ')}</span>
                            <span style={{ color: '#1abc9c', fontWeight: 'bold' }}>QEEG Analizi</span>
                        </div>
                        <div style={{ padding: '20px', display: 'flex', gap: '20px' }}>
                            <img 
                                src={`${exam.image_url}`} 
                                alt="QEEG" 
                                style={{ width: '150px', height: '150px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #eee', cursor: 'pointer' }}
                                onClick={() => setSelectedExam(exam)}
                            />
                            <div style={{ flex: 1, whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#334155', maxHeight: '200px', overflowY: 'auto' }}>
                                {exam.analysis_result}
                            </div>
                        </div>
                    </div>
                )) : (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Hələ ki müayinə qeydi yoxdur.</div>
                )}
            </div>

            {selectedExam && (
                <div className="patient-detail-modal-overlay" onClick={() => setSelectedExam(null)} style={{ zIndex: 2000 }}>
                    <div className="patient-detail-modal-content" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: '800px', height: 'auto', maxHeight: '90vh', overflowY: 'auto' }}>
                        <header className="patient-detail-header">
                            <h3 style={{ margin: 0 }}>Müayinə Detalları ({new Date(selectedExam.created_at).toLocaleDateString('az-AZ')})</h3>
                            <button onClick={() => setSelectedExam(null)} className="close-button">&times;</button>
                        </header>
                        <div style={{ padding: '25px', textAlign: 'center' }}>
                            <img src={selectedExam.image_url} alt="QEEG Böyük" style={{ width: '100%', borderRadius: '10px', border: '1px solid #eee', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                            <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
                                <div style={{ whiteSpace: 'pre-wrap', color: '#334155', fontSize: '1rem', lineHeight: '1.6' }}>
                                    <strong style={{ color: '#2c3e50', fontSize: '1.1rem' }}>Analiz Nəticəsi:</strong><br/>
                                    {selectedExam.analysis_result}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
   case 'testler':
    // 1. EĞER TEST FORMU AÇIKSA (Soru-Cevap Ekranı - Örn: GOBDÖ-2)
    if (showTestForm) {

     const isAGTE = testData.questions.some(q => Number(q.section_id) >= 4);
        if (isAGTE) {
            return renderAGTEForm();
        } else {
        const filteredQuestions = testData.questions.filter(q => childSpeaks || Number(q.section_id) !== 2);
        
        // Ham puanları hesapla
        const curRaw = { s1: 0, s2: 0, s3: 0 };
        filteredQuestions.forEach(q => {
            const val = answers[q.id] || 0;
            if (Number(q.section_id) === 1) curRaw.s1 += val;
            else if (Number(q.section_id) === 2) curRaw.s2 += val;
            else if (Number(q.section_id) === 3) curRaw.s3 += val;
        });

        // Standart ve İndeks puanlarını hesapla
        const cStd1 = getStandardScore(curRaw.s1, 1);
        const cStd2 = childSpeaks ? getStandardScore(curRaw.s2, 2) : 0;
        const cStd3 = getStandardScore(curRaw.s3, 3);
        const cSum = cStd1 + cStd2 + cStd3;
        const cObi = getOBI(cSum, childSpeaks);
        
        const answeredCount = filteredQuestions.filter(q => answers[q.id] !== undefined).length;
        const progress = filteredQuestions.length > 0 ? Math.round((answeredCount / filteredQuestions.length) * 100) : 0;

        return (
            <div className="tab-pane test-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="test-form-header" style={{ background: '#fff', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <h2 style={{ margin: 0, color: '#2c3e50' }}>{testData.testName || 'Değerlendirme Formu'}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Test Tarixi:</label>
                        <input 
                            type="date" 
                            value={testDate} 
                            onChange={(e) => setTestDate(e.target.value)}
                            style={{ 
                                padding: '5px 10px', 
                                border: '1px solid #ddd', 
                                borderRadius: '5px',
                                outline: 'none'
                            }}
                        />
                    </div>
                </div>

                <div style={{ backgroundColor: '#f8f9fa', padding: '12px 15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="checkbox" id="noSpeech" style={{ width: '18px', height: '18px' }} checked={!childSpeaks} onChange={(e) => {setChildSpeaks(!e.target.checked);}} />
                    <label htmlFor="noSpeech" style={{ fontWeight: '600', cursor: 'pointer' }}>Uşaq danışmır (Ünsiyyət bölməsini ötür)</label>
                </div>
                
                <div className="progress-container" style={{ marginBottom: '15px' }}>
                    <div className="progress-bar" style={{ width: `${progress}%` }}>{progress}%</div>
                </div>

                <div className="questions-list" style={{ flexGrow: 1, overflowY: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                    {filteredQuestions.map((q) => (
                        <div key={q.id} className="question-card" style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 10px', borderBottom: '1px solid #f0f0f0', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                            <div style={{ flex: '1 1 100%', marginBottom: '5px' }}><strong>{q.question_order || (testData.questions.findIndex(tq => tq.id === q.id) + 1)}.</strong> {q.question_text}</div>
                            <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'center' }}>
                                {[0,1,2,3].map(v => (
                                    <button key={v} onClick={() => handleScoreSelect(q.id, v)} className={`opt-btn ${answers[q.id] === v ? 'active' : ''}`} style={{ width: '34px', height: '34px', borderRadius: '50%', border: '1px solid #ccc', backgroundColor: answers[q.id] === v ? '#1abc9c' : '#fff', color: answers[q.id] === v ? '#fff' : '#333', fontWeight: 'bold', cursor: 'pointer' }}>{v}</button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="test-form-footer">
                    <div className="test-results-container">
                        <div className="test-main-score">
                            <span className="test-score-label">Cəm Std:</span>
                            <span className="test-score-value">{cSum}</span>
                        </div>
                        <div className="test-main-score">
                            <span className="test-score-label">OBİ:</span>
                            <span className="test-score-value" style={{ color: '#e74c3c' }}>{cObi}</span>
                        </div>
                        <div className="test-section-scores">
                            <div className="test-section-score-item">
                                <small>STR:</small>
                                <strong style={{ color: '#8bc34a' }}>{cStd1}S</strong>
                            </div>
                            {childSpeaks && (
                                <div className="test-section-score-item">
                                    <small>ÜNS:</small>
                                    <strong style={{ color: '#03a9f4' }}>{cStd2}S</strong>
                                </div>
                            )}
                            <div className="test-section-score-item">
                                <small>SOS:</small>
                                <strong style={{ color: '#f44336' }}>{cStd3}S</strong>
                            </div>
                        </div>
                    </div>
                    <div className="test-actions-container">
                        <button className="action-button cancel-button" onClick={() => {
                                if(window.confirm("Testi ləğv etmək istədiyinizə əminsiniz?")) {
                                    setShowTestForm(false);
                                    setAnswers({});
                                    setEditingResultId(null);
                                        setEditingResultTeacherFin(null);
                                }
                            }}>Ləğv Et</button>
                            {(!editingResultId || currentUser?.fin === editingResultTeacherFin || currentUser?.level > 0) && (
                                <button className="action-button" onClick={saveTest}>Yadda Saxla</button>
                            )}
                        </div>
                </div>
            </div>
        );
    }
    }

    // 2. EĞER TEST SEÇİM LİSTESİ AÇIKSA (Hangi testi yapmak istediğini seçtiği ekran)
    if (showSelectionList) {
        return (
            <div className="tab-pane">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                    <h2 style={{ margin: 0 }}>Təyin ediləcək testi seçin</h2>
                    <button className="action-button cancel-button" onClick={() => setShowSelectionList(false)}>Ləğv Et</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' }}>
                    {allTestTypes.map(test => (
                        <div key={test.id} onClick={() => openTestForm(test.id)} style={{ padding: '25px', border: '2px solid #e0e6ed', borderRadius: '15px', textAlign: 'center', cursor: 'pointer', transition: '0.3s', backgroundColor: '#fff' }} onMouseOver={(e) => e.currentTarget.style.borderColor = '#1abc9c'} onMouseOut={(e) => e.currentTarget.style.borderColor = '#e0e6ed'}>
                            <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>📝</div>
                            <strong style={{ color: '#2c3e50', fontSize: '1.1rem' }}>{test.test_name}</strong>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // 3. ANA EKRAN: TEST GEÇMİŞİ (Kayıtlı sonuçların listelendiği analiz ekranı)
    return (
        <div className="tab-pane">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                <h2 style={{ margin: 0, color: '#2c3e50' }}>Test Tarixçəsi və Analizi</h2>
                <button className="action-button" onClick={handleNewTestClick} disabled={testLoading}>
                    {testLoading ? 'Yüklənir...' : 'Yeni Test Əlavə Et'}
                </button>
            </div>

            {pastResults && pastResults.length > 0 ? (
                <div className="results-list">
                 {pastResults.map(res => {
    let details = {};
    try {
        details = typeof res.details === 'string' ? JSON.parse(res.details) : (res.details || {});
    } catch (e) { details = {}; }

    const isAGTE = res.test_name && res.test_name.includes('AGTE');
    const isOpen = openResultId === res.id;
    const prob = !isAGTE ? getProbability(res.index_score) : null;

    return (
        <div key={res.id} style={{ border: '1px solid #e0e6ed', borderRadius: '12px', marginBottom: '15px', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            
            {/* --- DROPDOWN BAŞLIĞI (Həmişə görünən hissə) --- */}
            <div 
                onClick={() => setOpenResultId(isOpen ? null : res.id)}
                className="result-item-header"
                style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '15px 20px', 
                    backgroundColor: isAGTE ? '#f0fdf4' : '#f8fafc', 
                    cursor: 'pointer',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '15px',
                    transition: 'background 0.3s'
                }}
            >
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: '80px' }}>
                        <label style={{ fontSize: '0.65rem', color: '#94a3b8', display: 'block', fontWeight: 'bold', textTransform: 'uppercase' }}>Tarix</label>
                        <span style={{ fontWeight: '600', color: '#334155' }}>{new Date(res.created_at).toLocaleDateString('az-AZ')}</span>
                    </div>
                    <div style={{ minWidth: '100px' }}>
                        <label style={{ fontSize: '0.65rem', color: '#94a3b8', display: 'block', fontWeight: 'bold', textTransform: 'uppercase' }}>{isAGTE ? 'AGTE' : 'GOBDÖ-2'}</label>
                        <span style={{ fontWeight: '800', color: isAGTE ? '#16a34a' : '#e74c3c' }}>
                            {isAGTE ? `${details.generalAge || '-'} ay` : `OBİ: ${res.index_score}`}
                        </span>
                    </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {!isAGTE && (
                        <div style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 'bold', color: prob.color, backgroundColor: prob.bg }}>
                            {prob.text}
                        </div>
                    )}
                    <span style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s', fontSize: '12px', color: '#94a3b8' }}>▼</span>
                </div>
            </div>

            {/* --- DETALLAR (Yalnız üstünə basanda açılan hissə) --- */}
            {isOpen && (
                <div style={{ padding: '20px', borderTop: '1px solid #eee', background: '#fff' }}>
                    {isAGTE ? (
                        /* AGTE Detalları - Barlar */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '200px', paddingBottom: '20px', overflowX: 'auto', gap: '10px' }}>
                                <AGTE_Bar label="Dil-Biliş" age={details.age4} raw={details.raw4} color="#8bc34a" />
                                <AGTE_Bar label="Kiçik Motor" age={details.age5} raw={details.raw5} color="#03a9f4" />
                                <AGTE_Bar label="Böyük Motor" age={details.age6} raw={details.raw6} color="#f44336" />
                                <AGTE_Bar label="Sosial" age={details.age7} raw={details.raw7} color="#ffeb3b" />
                                <div style={{ minWidth: '1px', height: '100%', background: '#eee' }}></div>
                                <AGTE_Bar label="Ümumi" age={details.generalAge} raw={res.total_raw_score} color="#2c3e50" isTotal={true} />
                            </div>
                            <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                <button 
                                    className="action-button" 
                                    style={{ backgroundColor: '#10b981', flex: 1 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewTestReadonly(res);
                                    }}
                                >
                                    Baxış (Print)
                                </button>
                                <button 
                                    className="action-button" 
                                    style={{ backgroundColor: '#34495e', flex: 1 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewDetails(res);
                                    }}
                                >
                                    Redaktə Et
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* GOBDÖ Detalları - Qutular */
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
                            <InfoBox label="Stereotip Davranışlar" raw={details.raw1} std={details.std1} />
                            {details.childSpeaks !== false && (
                                <InfoBox label="Ünsiyyət" raw={details.raw2} std={details.std2} />
                            )}
                            <InfoBox label="Sosial Qarşılıqlı Əlaqə" raw={details.raw3} std={details.std3} />
                            <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#f8fafc', borderLeft: '4px solid #334155', textAlign: 'center' }}>
                                <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '4px' }}>Yekun Standart Cəm</span>
                                <div style={{ fontWeight: '800', color: '#1e293b', fontSize: '1.1rem' }}>{res.total_standard_score}</div>
                            </div>
                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', marginTop: '10px' }}>
                                <button 
                                    className="action-button" 
                                    style={{ backgroundColor: '#10b981', flex: 1 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewTestReadonly(res);
                                    }}
                                >
                                    Baxış (Print)
                                </button>
                                <button 
                                    className="action-button" 
                                    style={{ backgroundColor: '#34495e', flex: 1 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewDetails(res);
                                    }}
                                >
                                    Redaktə Et
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* Bütün testlərdə ortaq: Rəy və ya qeydlər hissəsi əlavə edilə bilər */}
                    <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '6px', fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                        * Bu test nəticəsi avtomatik hesablanmışdır və mütəxəssis tərəfindən təsdiq edilməlidir.
                    </div>
                </div>
            )}
        </div>
    );
})}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '80px 20px', backgroundColor: '#f8fafc', borderRadius: '15px', border: '2px dashed #e2e8f0' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📋</div>
                    <p style={{ fontSize: '1.1rem', color: '#64748b' }}>Hələ ki, heç bir test nəticəsi qeyd edilməyib.</p>
                </div>
            )}
        </div>
    );
            case 'proqram':
                return (
                    <div className="tab-pane">
                        <h2>Təlim Proqramı</h2>
                        <p>Xəstənin aldığı dərslər və seans məlumatları burada yer alacaq.</p>
                        <p><strong>Dərs:</strong> {formatText(patient.dersAdi)}</p>
                    </div>
                );
           case 'inkisaf':
                // 1. Dataları Tarixə Görə Qruplaşdırırıq (Həm AGTE, həm GOBDÖ eyni qrafikdə olsun deyə)
                const groupedData = {};
                
                pastResults.forEach(res => {
                    const dateStr = new Date(res.created_at).toLocaleDateString('az-AZ');
                    
                    if (!groupedData[dateStr]) {
                        // O tarixdəki Xronoloji Yaşı tapırıq
                        const testDate = new Date(res.created_at);
                        const birthDate = new Date(patient.yas);
                        let realAgeMonths = (testDate.getFullYear() - birthDate.getFullYear()) * 12 + (testDate.getMonth() - birthDate.getMonth());
                        realAgeMonths = realAgeMonths <= 0 ? 0 : realAgeMonths;

                        groupedData[dateStr] = {
                            id: res.id,
                            tarix: dateStr,
                            tamTarix: testDate,
                            xronolojiYas: realAgeMonths,
                            inkisafYasi: null, // AGTE üçün
                            obiScore: null,    // GOBDÖ üçün
                            agteDetallar: null,
                            gobdoDetallar: null,
                            tests: [],
                            isRealTest: true
                        };
                    }

                    let details = {};
                    try { details = typeof res.details === 'string' ? JSON.parse(res.details) : (res.details || {}); } catch(e){}

                    groupedData[dateStr].tests.push(res);

                    // Əgər AGTE testidirsə, yaş məlumatlarını əlavə et
                    if (res.test_name && res.test_name.includes('AGTE')) {
                        groupedData[dateStr].inkisafYasi = details.generalAge || 0;
                        groupedData[dateStr].agteDetallar = details;
                    } 
                    // Əgər GOBDÖ testidirsə, OBİ məlumatlarını əlavə et
                    else if (res.test_name && res.test_name.includes('GOBDÖ')) {
                        groupedData[dateStr].obiScore = res.index_score || 0;
                        groupedData[dateStr].gobdoDetallar = details;
                    }
                });

                const rawChartData = Object.values(groupedData).sort((a, b) => a.tamTarix - b.tamTarix);

                // GOBDÖ Müqayisə Analizi (Ən birinci və ən son testlər arasında)
                const gobdoHistory = rawChartData.filter(d => d.obiScore !== null);
                let gobdoComparison = null;
                if (gobdoHistory.length >= 2) {
                    const latest = gobdoHistory[gobdoHistory.length - 1];
                    const prev = gobdoHistory[0]; 
                    // Tarix fərqini ay olaraq hesabla
                    const diffMonths = (latest.tamTarix.getFullYear() - prev.tamTarix.getFullYear()) * 12 + (latest.tamTarix.getMonth() - prev.tamTarix.getMonth());
                    const isImproved = latest.obiScore < prev.obiScore; // OBİ-nin düşməsi inkişafdır (uğurdur)
                    gobdoComparison = {
                        text: `İlkin testdən bu yana keçən ${diffMonths} ayda OBİ nəticəsi ${prev.obiScore}-dən ${latest.obiScore} indeksə ${isImproved ? 'düşmüşdür' : 'qalxmışdır'}.`,
                        color: isImproved ? '#10b981' : '#ef4444'
                    };
                }

                // AGTE Müqayisə Analizi (İnkişaf sürəti vs Zaman)
                const agteHistory = rawChartData.filter(d => d.inkisafYasi !== null);
                let agteComparison = null;
                if (agteHistory.length >= 2) {
                    const latest = agteHistory[agteHistory.length - 1];
                    const prev = agteHistory[0];
                    const diffMonths = (latest.tamTarix.getFullYear() - prev.tamTarix.getFullYear()) * 12 + (latest.tamTarix.getMonth() - prev.tamTarix.getMonth());
                    const devGain = latest.inkisafYasi - prev.inkisafYasi;
                    const progressValue = devGain - diffMonths;

                    let statusText = "";
                    let color = "";
                    if (progressValue > 0) {
                        statusText = "Ugurlu inkişaf";
                        color = "#10b981";
                    } else if (progressValue === 0) {
                        statusText = "İnkişaf gözləmlənilməyib";
                        color = "#f59e0b";
                    } else {
                        statusText = "İnkişafdan geri qalıb";
                        color = "#ef4444";
                    }
                    agteComparison = {
                        text: `${diffMonths} ay ərzində ümumi inkişaf ${devGain} ay artmışdır. Yekun: ${statusText}.`,
                        color: color
                    };
                }

                // 2. Qrafik üçün "Doğuş" nöqtəsini əlavə edirik
                const chartData = rawChartData.length > 0 ? [
                    { id: 'birth', tarix: 'Doğuş', xronolojiYas: 0, inkisafYasi: 0, obiScore: null, isRealTest: false },
                    ...rawChartData
                ] : [];

                const activeTestDetail = selectedDevTest || (rawChartData.length > 0 ? rawChartData[rawChartData.length - 1] : null);

                // 3. Fərqə (Gap) Görə Dinamik Rəng Təyini
                const getDynamicColor = (real, dev) => {
                    if (real == null || dev == null) return '#3b82f6'; // Standart Mavi
                    const gap = real - dev;
                    if (gap <= 4) return '#10b981'; // Yaşıl (Normal/Yaxşı)
                    if (gap > 4 && gap <= 10) return '#f59e0b'; // Narıncı (Diqqət)
                    return '#ef4444'; // Qırmızı (Kritik Geriləmə)
                };

                const chartThemeColor = activeTestDetail 
                    ? getDynamicColor(activeTestDetail.xronolojiYas, activeTestDetail.inkisafYasi)
                    : '#3b82f6';

                return (
                    <div className="tab-pane">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, color: '#2c3e50' }}>İnkişaf və Risk Dinamikası</h2>
                            <button className="action-button" onClick={() => setActiveTab('testler')}>Yeni Test Et</button>
                        </div>

                        {chartData.length > 0 ? (
                            <>
                            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                                
                                {/* VALİDEYN ÜÇÜN DİNAMİK XÜLASƏ */}
                                {activeTestDetail && activeTestDetail.isRealTest && (
                                    <div style={{ padding: '15px', backgroundColor: `${chartThemeColor}15`, borderRadius: '8px', borderLeft: `5px solid ${chartThemeColor}`, marginBottom: '20px' }}>
                                        <h4 style={{ margin: '0 0 5px 0', color: chartThemeColor }}>Uzman Rəyi (Xülasə):</h4>
                                        <p style={{ margin: 0, fontSize: '1rem', color: '#334155', lineHeight: '1.5' }}>
                                            Pasiyentin <strong>{activeTestDetail.tarix}</strong> tarixində real yaşı <strong>{activeTestDetail.xronolojiYas} ay</strong>dır. 
                                            {activeTestDetail.inkisafYasi !== null && (
                                                <span> Ümumi inkişaf səviyyəsi <strong>{activeTestDetail.inkisafYasi} ay</strong> olaraq qiymətləndirilmişdir. </span>
                                            )}
                                            {activeTestDetail.inkisafYasi !== null && activeTestDetail.xronolojiYas > activeTestDetail.inkisafYasi ? (
                                                <strong style={{ color: chartThemeColor }}> Bu, standartdan {activeTestDetail.xronolojiYas - activeTestDetail.inkisafYasi} ay geridə qalma deməkdir.</strong>
                                            ) : activeTestDetail.inkisafYasi !== null ? (
                                                <strong style={{ color: chartThemeColor }}> İnkişaf yaşa uyğundur!</strong>
                                            ) : null}
                                            {activeTestDetail.obiScore !== null && (
                                                <span style={{ display: 'block', marginTop: '5px', fontWeight: 'bold', color: activeTestDetail.obiScore > 85 ? '#ef4444' : '#8b5cf6' }}>
                                                    📌 GOBDÖ-2 Nəticəsi: OBİ Riski {activeTestDetail.obiScore} xaldır.
                                                </span>
                                            )}
                                            {/* GOBDÖ Tərəqqi Analizi */}
                                            {gobdoComparison && activeTestDetail.obiScore !== null && (
                                                <span style={{ display: 'block', marginTop: '10px', fontWeight: 'bold', color: gobdoComparison.color, padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: `1px solid ${gobdoComparison.color}`, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                                    📊 GOBDÖ Analizi: {gobdoComparison.text}
                                                </span>
                                            )}
                                            {/* AGTE Tərəqqi Analizi */}
                                            {agteComparison && activeTestDetail.inkisafYasi !== null && (
                                                <span style={{ display: 'block', marginTop: '10px', fontWeight: 'bold', color: agteComparison.color, padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: `1px solid ${agteComparison.color}`, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                                    📈 AGTE Analizi: {agteComparison.text}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                
                                {/* ÇOX ÖLÇÜLÜ QRAFİK (ComposedChart) */}
                                <div style={{ width: '100%', height: '350px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorDynamic" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={chartThemeColor} stopOpacity={0.6}/>
                                                    <stop offset="95%" stopColor={chartThemeColor} stopOpacity={0}/>
                                                </linearGradient>
                                                <linearGradient id="colorHedef" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <XAxis dataKey="tarix" stroke="#94a3b8" fontSize={12} />
                                            
                                            {/* Sol Ox - Yaş Üçün */}
                                            <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} label={{ value: 'Yaş (Ay)', angle: -90, position: 'insideLeft', fill: '#94a3b8' }} />
                                            
                                            {/* Sağ Ox - OBİ Üçün */}
                                            <YAxis yAxisId="right" orientation="right" stroke="#8b5cf6" fontSize={12} domain={[0, 150]} label={{ value: 'OBİ İndeksi', angle: 90, position: 'insideRight', fill: '#8b5cf6' }} />
                                            
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
                                            
                                            {/* Hədəf Yaş Xətti */}
                                            <Area yAxisId="left" type="monotone" dataKey="xronolojiYas" name="Hədəf (Real Yaş)" stroke="#10b981" fill="url(#colorHedef)" strokeWidth={2} strokeDasharray="5 5" />
                                            
                                            {/* İnkişaf Xətti (Dinamik Rəngli) */}
                                            <Area 
                                                yAxisId="left" 
                                                type="monotone" 
                                                dataKey="inkisafYasi" 
                                                name="İnkişaf Yaşı" 
                                                stroke={chartThemeColor} 
                                                fill="url(#colorDynamic)" 
                                                strokeWidth={3} 
                                                connectNulls={true}
                                                activeDot={{ r: 8, cursor: 'pointer', onClick: (e, payload) => { if(payload?.payload?.isRealTest) setSelectedDevTest(payload.payload); } }} 
                                            />

                                            {/* OBİ Xətti (Sağ oxa bağlıdır) */}
                                            <Line 
                                                yAxisId="right" 
                                                type="monotone" 
                                                dataKey="obiScore" 
                                                name="OBİ Riski" 
                                                stroke="#8b5cf6" 
                                                strokeWidth={3}
                                                dot={{ r: 5, fill: '#8b5cf6' }}
                                                activeDot={{ r: 8 }}
                                                connectNulls={true}
                                            />

                                            {/* Seçilmiş test nöqtəsini qrafikdə fərqləndiririk */}
                                            {activeTestDetail && activeTestDetail.inkisafYasi !== null && (
                                                <ReferenceDot 
                                                    yAxisId="left"
                                                    x={activeTestDetail.tarix} 
                                                    y={activeTestDetail.inkisafYasi} 
                                                    r={7} 
                                                    fill={chartThemeColor} 
                                                    stroke="#fff" 
                                                    strokeWidth={3}
                                                />
                                            )}
                                            {activeTestDetail && activeTestDetail.obiScore !== null && (
                                                <ReferenceDot 
                                                    yAxisId="right"
                                                    x={activeTestDetail.tarix} 
                                                    y={activeTestDetail.obiScore} 
                                                    r={7} 
                                                    fill="#8b5cf6" 
                                                    stroke="#fff" 
                                                    strokeWidth={3}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* EDİLEN TESTLƏRİN SİYAHISI (COLLAPSE SİSTEMİ) */}
                            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <h4 style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Keçirilmiş Testlər Siyahısı</h4>
                                {rawChartData.slice().reverse().map(group => {
                                    const isSelected = activeTestDetail?.tarix === group.tarix;
                                    const testNames = group.tests.map(t => t.test_name.replace('Ankara Gelişim Tarama Envanteri', 'AGTE').replace('Giller Otistik Bozukluk Derecelendirme Ölçeği', 'GOBDÖ')).join(", ");
                                    
                                    return (
                                        <div key={group.tarix} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', backgroundColor: isSelected ? '#f8fafc' : '#fff', transition: '0.3s' }}>
                                            <div 
                                                onClick={() => setSelectedDevTest(group)}
                                                style={{ padding: '12px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: isSelected ? '5px solid #3b82f6' : '5px solid transparent' }}
                                            >
                                                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                                    <strong style={{ color: isSelected ? '#1e293b' : '#64748b' }}>{group.tarix}</strong>
                                                    <span style={{ fontSize: '0.9rem', color: isSelected ? '#3b82f6' : '#94a3b8', fontWeight: isSelected ? 'bold' : 'normal' }}>{testNames}</span>
                                                </div>
                                                <span style={{ transform: isSelected ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s', color: '#94a3b8' }}>▼</span>
                                            </div>
                                            
                                            {isSelected && (
                                                <div style={{ padding: '15px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '15px', animation: 'fadeIn 0.3s' }}>
                                                    
                                                    {/* AGTE Detalları (İnkişaf Sahələri) */}
                                                    {group.agteDetallar && (
                                                        <div>
                                                            <h5 style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: '#64748b', fontWeight: 'bold' }}>AGTE İnkişaf Sahələri:</h5>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                                                                {[
                                                                    { label: 'Dil-Bilişsel', value: group.agteDetallar.age4 || 0, color: '#8bc34a' },
                                                                    { label: 'İncə Motor', value: group.agteDetallar.age5 || 0, color: '#03a9f4' },
                                                                    { label: 'Kobud Motor', value: group.agteDetallar.age6 || 0, color: '#f44336' },
                                                                    { label: 'Sosial Bacarıq', value: group.agteDetallar.age7 || 0, color: '#ffeb3b' }
                                                                ].map((item, idx) => {
                                                                    const safeRealAge = group.xronolojiYas > 0 ? group.xronolojiYas : 1;
                                                                    const percent = Math.min(Math.round((item.value / safeRealAge) * 100), 100);
                                                                    return (
                                                                        <div key={idx}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '4px' }}>
                                                                                <span style={{ fontWeight: '600', color: '#334155' }}>{item.label}</span>
                                                                                <span style={{ fontWeight: 'bold', color: percent < 50 ? '#ef4444' : percent < 80 ? '#f59e0b' : '#10b981' }}>{item.value} ay ({percent}%)</span>
                                                                            </div>
                                                                            <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                                                                                <div style={{ width: `${percent}%`, height: '100%', backgroundColor: item.color, transition: 'width 1s ease-in-out' }}></div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* GOBDÖ Detalları (Risk Analizi) */}
                                                    {group.gobdoDetallar && (
                                                        <div style={{ paddingTop: group.agteDetallar ? '10px' : 0, borderTop: group.agteDetallar ? '1px dashed #e2e8f0' : 'none' }}>
                                                            <h5 style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: '#64748b', fontWeight: 'bold' }}>GOBDÖ-2 Risk Analizi:</h5>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                                                                <div style={{ padding: '8px', backgroundColor: '#fff', borderRadius: '6px', borderLeft: '3px solid #8b5cf6', border: '1px solid #f1f5f9' }}>
                                                                    <span style={{ fontSize: '0.65rem', color: '#94a3b8', display: 'block' }}>Stereotip Davranış</span>
                                                                    <span style={{ fontWeight: '700', fontSize: '0.8rem', color: '#1e293b' }}>{group.gobdoDetallar.std1 || 0} Std</span>
                                                                </div>
                                                                {group.gobdoDetallar.childSpeaks !== false && (
                                                                    <div style={{ padding: '8px', backgroundColor: '#fff', borderRadius: '6px', borderLeft: '3px solid #8b5cf6', border: '1px solid #f1f5f9' }}>
                                                                        <span style={{ fontSize: '0.65rem', color: '#94a3b8', display: 'block' }}>Ünsiyyət</span>
                                                                        <span style={{ fontWeight: '700', fontSize: '0.8rem', color: '#1e293b' }}>{group.gobdoDetallar.std2 || 0} Std</span>
                                                                    </div>
                                                                )}
                                                                <div style={{ padding: '8px', backgroundColor: '#fff', borderRadius: '6px', borderLeft: '3px solid #8b5cf6', border: '1px solid #f1f5f9' }}>
                                                                    <span style={{ fontSize: '0.65rem', color: '#94a3b8', display: 'block' }}>Sosial Əlaqə</span>
                                                                    <span style={{ fontWeight: '700', fontSize: '0.8rem', color: '#1e293b' }}>{group.gobdoDetallar.std3 || 0} Std</span>
                                                                </div>
                                                                <div style={{ padding: '8px', backgroundColor: '#fcf8ff', borderRadius: '6px', borderLeft: '3px solid #ef4444', textAlign: 'center', border: '1px solid #e9d5ff' }}>
                                                                    <span style={{ fontSize: '0.65rem', color: '#6b21a8', fontWeight: 'bold' }}>OBİ İndeksi</span>
                                                                    <div style={{ fontWeight: '900', color: '#ef4444', fontSize: '0.9rem' }}>{group.obiScore}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Detallı Cavabları Görmək üçün düymələr */}
                                                    <div style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                                                        {group.tests.map(t => (
                                                            <button key={t.id} className="action-button" style={{ padding: '5px 12px', fontSize: '0.65rem', height: 'auto', backgroundColor: '#475569', opacity: 0.9 }} onClick={(e) => { e.stopPropagation(); fetchResultAnswers(t.id); }}>
                                                                {t.test_name.includes('AGTE') ? 'AGTE Cavabları' : 'GOBDÖ Cavabları'}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                                <span style={{ fontSize: '3rem' }}>📈</span>
                                <p style={{ color: '#64748b', marginTop: '10px' }}>Qrafik yaratmaq üçün hələ test nəticəsi yoxdur.</p>
                            </div>
                        )}
                        <style>{`
                            @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
                        `}</style>
                    </div>
                );
            case 'videolar':
                return (
                    <div className="tab-pane">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0 }}>Proqram Videoları</h2>
                            <button className="action-button" onClick={() => setShowVideoModal(true)}>Yeni Video Yüklə</button>
                        </div>
                        <p>Xəstənin inkişaf prosesi və proqramı ilə bağlı videoları buradan izləyə bilərsiniz.</p>
                        
                        {loadingVideos ? (
                            <p>Videolar yüklənir...</p>
                        ) : videos.length > 0 ? (
                            <div className="videos-grid" style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', 
                                gap: '16px', 
                                marginTop: '20px' 
                            }}>
                                {videos.map(video => {
                                    if (!video.link) return null;
                                    return (
                                        <div key={video.id} className="video-card" style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
                                            {video.status === 'processing' ? (
                                                <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f5f9', color: '#64748b', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                    Video hazırlanır...
                                                </div>
                                            ) : (
                                                <div style={{ 
                                                    position: 'relative', 
                                                    width: '100%', 
                                                    aspectRatio: video.width && video.height ? `${video.width} / ${video.height}` : '16 / 9',
                                                    maxHeight: '80vh', // Mobil ekranlarda videonun hədsiz uzun olmasının qarşısını alır
                                                    backgroundColor: '#000',
                                                    overflow: 'hidden'
                                                }}>
                                                    <iframe 
                                                        src={getEmbedLink(video.link)} 
                                                        style={{ 
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            border: 'none'
                                                        }}
                                                        allow="autoplay" 
                                                        allowFullScreen
                                                        title="Patient Video"
                                                    ></iframe>
                                                </div>
                                            )}
                                        <div style={{ padding: '10px', fontSize: '0.9rem', color: '#666', backgroundColor: '#f8f9fa' }}>
                                            Yüklənmə tarixi: {new Date(video.date).toLocaleDateString()}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p>Hələ heç bir video yüklənməyib.</p>
                        )}

                        {showVideoModal && (
                            <div className="patient-detail-modal-overlay" onClick={() => !videoUploading && setShowVideoModal(false)} style={{ zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div className="patient-detail-modal-content" onClick={e => e.stopPropagation()} style={{ width: '400px', maxWidth: '90%', padding: '20px', height: 'auto' }}>
                                    <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#34495e' }}>Yeni Video Yüklə</h3>
                                    <input
                                        type="file"
                                        accept="video/*"
                                        onChange={handleVideoChange}
                                        ref={videoInputRef}
                                        style={{ display: 'block', marginBottom: '20px', width: '100%', padding: '10px', border: '1px dashed #ccc', borderRadius: '4px' }}
                                        disabled={videoUploading} // Disable input during upload
                                    />
                                    {videoUploading && (
                                        <div className="progress-container">
                                            <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                                                {uploadProgress}%
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
                                                {uploadProgress < 100 
                                                    ? `Sürət: ${uploadSpeed} MB/s | Qalan vaxt: ${timeLeft} san.` 
                                                    : "Fayl qəbul edildi. Drive-a yüklənir, zəhmət olmasa gözləyin..."}
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button 
                                            className="action-button cancel-button" 
                                            onClick={() => setShowVideoModal(false)}
                                            disabled={videoUploading}
                                        >
                                            Ləğv Et
                                        </button>
                                        <button 
                                            className="action-button" 
                                            onClick={uploadVideo} 
                                            disabled={!videoFile || videoUploading}
                                            style={{ opacity: (!videoFile || videoUploading) ? 0.6 : 1, cursor: (!videoFile || videoUploading) ? 'not-allowed' : 'pointer' }}
                                        >
                                            {videoUploading ? 'Yüklənir...' : 'Yüklə'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
        case 'rey':
                return (
                <div className="tab-pane">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ margin: 0, color: '#2c3e50' }}>Mütəxəssis Rəyləri</h2>
                        {canAddReview && !isAddingReview && (
                            <button className="action-button" onClick={() => setIsAddingReview(true)}>+ Rəy Əlavə Et</button>
                        )}
                    </div>

                    {isAddingReview && (
                        <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                            <textarea 
                                className="table-textarea" 
                                rows="4" 
                                placeholder="Pasiyent haqqında rəyinizi yazın..."
                                value={newReviewText}
                                onChange={(e) => setNewReviewText(e.target.value)}
                            ></textarea>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
                                <button className="action-button cancel-button" onClick={() => { setIsAddingReview(false); setNewReviewText(""); }}>Ləğv Et</button>
                                <button className="action-button" onClick={handleAddReview}>Yadda Saxla</button>
                            </div>
                        </div>
                    )}

                    {loadingReviews ? (
                        <p>Yüklənir...</p>
                    ) : reviews.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {reviews.map(r => (
                                <div key={r.id} style={{ padding: '15px', border: '1px solid #e0e6ed', borderRadius: '8px', backgroundColor: editingReview?.id === r.id ? '#f8fafc' : '#fff' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <strong style={{ color: '#34495e' }}>{r.teacher_name || 'Naməlum'}</strong>
                                        <span style={{ fontSize: '0.85rem', color: '#95a5a6' }}>{new Date(r.created_at).toLocaleString('az-AZ')}</span>
                                    </div>
                                    {editingReview?.id === r.id ? (
                                        <div>
                                            <textarea 
                                                className="table-textarea" 
                                                rows="4" 
                                                value={editingReview.text}
                                                onChange={(e) => setEditingReview({ ...editingReview, text: e.target.value })}
                                            ></textarea>
                                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
                                                <button className="action-button cancel-button" onClick={() => setEditingReview(null)}>Ləğv Et</button>
                                                <button className="action-button" onClick={handleEditReview}>Yadda Saxla</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <p style={{ margin: 0, color: '#555', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{r.review_text}</p>
                                    )}
                                    {((currentUser?.fin === r.teacher_fin || currentUser?.level > 0) && !editingReview) && (
                                        <button className="action-button" style={{ padding: '4px 10px', fontSize: '0.75rem', marginTop: '10px' }} onClick={() => setEditingReview({ id: r.id, text: r.review_text })}>Redaktə Et</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{ color: '#94a3b8' }}>Hələ heç bir rəy yazılmayıb.</p>
                    )}
                    </div>
                );
        default:
            return null;
        }
    };

    return (
        <div className="patient-detail-modal-overlay" onClick={isEditing ? undefined : onClose}>
            <div className="patient-detail-modal-content" onClick={e => e.stopPropagation()}>
                <header className="patient-detail-header">
                    <h1>{formatText(formData.ad)}</h1>
                    <button onClick={onClose} className="close-button">&times;</button>
                </header> 
                <div className="patient-detail-body">
                    <aside className={`patient-sidebar ${sidebarOpen ? 'expanded' : 'collapsed'}`}>
                        <div className="sidebar-mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                            <span style={{ fontWeight: '600' }}>Xəstə Məlumatları</span>
                            <span className="toggle-icon">{sidebarOpen ? '▲' : '▼'}</span>
                        </div>
                        <div className="sidebar-content">
                    <div className={`patient-photo-container ${isEditing ? 'editable' : ''}`} onClick={handlePhotoClick}>
                            {photoPreview ? (
                                <img src={photoPreview} alt="Önizləmə" className="patient-photo" />
                            ) : patient.photoUrl ? (
                                <img src={patient.photoUrl} alt={formatText(patient.ad)} className="patient-photo" />
                            ) : (
                                <div className="patient-photo-placeholder">
                                    <span>NO IMAGE</span>
                                </div>
                            )}
                            {isEditing && (
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoChange}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                            />
                            )}
                        </div>
                        <div className="sidebar-header">
                            <h3>Xəstə Məlumatları</h3>
                            <button onClick={isEditing ? handleSave : () => setIsEditing(true)} className="edit-button">
                                {isEditing ? 'Yadda Saxla' : 'Redaktə Et'}
                            </button>
                        </div>
                        <div className="info-grid">
                            {isEditing ? (
                                <>
                                    <div className="info-item"><label>Ad:</label><input type="text" name="ad" value={formData.ad} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>Doğum tarixi:</label><input type="date" name="yas" value={formData.yas} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>Telefon:</label><input type="text" name="telefon" value={formData.telefon} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>Ünvan:</label><input type="text" name="unvan" value={formData.unvan} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>Valideyn:</label><input type="text" name="valideyn" value={formData.valideyn} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>Seriya №:</label><input type="text" name="seriya" value={formData.seriya} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>FİN:</label><input type="text" name="fin" value={formData.fin} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>Endirim (%):</label><input type="number" name="endirim" value={formData.endirim} onChange={handleInputChange} /></div>
                                    <div className="info-item"><label>Balans:</label><input type="text" name="balans" value={formData.balans} onChange={handleInputChange} /></div>
                                    <div className="info-item info-item-full"><label>Əlavə məlumat:</label><textarea rows="3" name="info" value={formData.info} onChange={handleInputChange}></textarea></div>
                                </>
                            ) : (
                                <>
                                    <div className="info-item"><strong>Doğum tarixi:</strong><span>{patient.yas ? new Date(patient.yas).toLocaleDateString() : 'Qeyd edilməyib'}</span></div>
                                    <div className="info-item"><strong>Telefon:</strong><span>{formatText(patient.telefon)}</span></div>
                                    <div className="info-item"><strong>Ünvan:</strong><span>{formatText(patient.unvan)}</span></div>
                                    <div className="info-item"><strong>Valideyn:</strong><span>{formatText(patient.valideyn)}</span></div>
                                    <div className="info-item"><strong>Seriya №:</strong><span>{formatText(patient.seriya)}</span></div>
                                    <div className="info-item"><strong>FİN:</strong><span>{formatText(patient.fin)}</span></div>
                                    <div className="info-item"><strong>Endirim:</strong><span>{patient.endirim ? `${patient.endirim}%` : '0%'}</span></div>
                                    <div className="info-item"><strong>Balans:</strong><span>{formatText(patient.balans)}</span></div>
                                    <div className="info-item info-item-full"><strong>Tanı:</strong><span>{formatText(patient.diagnosis, 'Tanı əlavə edilməyib')}</span></div>
                                    <div className="info-item info-item-full"><strong>Əlavə məlumat:</strong><span>{patient.info || 'Qeyd edilməyib'}</span></div>
                                </>
                            )}
                        </div>
                        {!isEditing && (
                            <button className="action-button full-width">Yeni Tanı Əlavə Et</button>
                        )}
                        {isEditing && (
                             <button onClick={() => setIsEditing(false)} className="action-button full-width cancel-button">
                                Ləğv Et
                            </button>
                        )}
                        </div>
                    </aside>
                    <main className="patient-main-content">
                        <nav className="patient-tabs">
                            <a onClick={() => setActiveTab('deyer')} className={activeTab === 'deyer' ? 'active' : ''}>Deyerlendirme</a>
                            <a onClick={() => setActiveTab('inkisaf')} className={activeTab === 'inkisaf' ? 'active' : ''}>İnkişaf Prosesi</a>
                            <a onClick={() => setActiveTab('testler')} className={activeTab === 'testler' ? 'active' : ''}>Testlər</a>
                            <a onClick={() => setActiveTab('proqram')} className={activeTab === 'proqram' ? 'active' : ''}>Proqram</a>
                        <a onClick={() => setActiveTab('muayineler')} className={activeTab === 'muayineler' ? 'active' : ''}>QEEG</a>
                            <a onClick={() => setActiveTab('videolar')} className={activeTab === 'videolar' ? 'active' : ''}>Videolar</a>
                        <a onClick={() => setActiveTab('rey')} className={activeTab === 'rey' ? 'active' : ''}>Mütəxəssis Rəyi</a>
                        </nav>
                        <div className="patient-tab-content">
                            {renderTabContent()}
                        </div>
                    </main>
                </div>
            </div>

            {/* Köhnə Cavablar Overlay (Modal) */}
            {viewingAnswers && (
                <div className="patient-detail-modal-overlay" onClick={() => setViewingAnswers(null)} style={{ zIndex: 2000 }}>
                    <div className="patient-detail-modal-content" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: '700px', height: 'auto', maxHeight: '80vh', overflowY: 'auto' }}>
                        <header className="patient-detail-header">
                            <h3 style={{ margin: 0 }}>Test Cavabları</h3>
                            <button onClick={() => setViewingAnswers(null)} className="close-button">&times;</button>
                        </header>
                        <div style={{ padding: '20px' }}>
                            {viewingAnswers.map((item, idx) => (
                                <div key={idx} style={{ padding: '12px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ flex: 1, paddingRight: '15px' }}>
                                        <small style={{ color: '#1abc9c', fontWeight: 'bold', display: 'block' }}>{item.section_name}</small>
                                        <span style={{ fontSize: '0.95rem', color: '#2c3e50' }}>{idx + 1}. {item.question_text}</span>
                                    </div>
                                    <div style={{ 
                                        width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f1f3f5', 
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#2c3e50', border: '2px solid #1abc9c'
                                    }}>
                                        {item.score}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Read-Only & Print Test Result Overlay */}
            {viewingTestResult && (
                <div className="patient-detail-modal-overlay" onClick={() => setViewingTestResult(null)} style={{ zIndex: 2000 }}>
                    <div className="patient-detail-modal-content" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: '800px', height: 'auto', maxHeight: '90vh', overflowY: 'auto' }}>
                        <header className="patient-detail-header" data-html2canvas-ignore="true">
                            <h3 style={{ margin: 0 }}>Test Nəticəsi (Baxış)</h3>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button className="action-button" onClick={() => window.print()} style={{ margin: 0, padding: '5px 15px', backgroundColor: '#3b82f6' }}>Çap Et / PDF</button>
                                <button onClick={() => setViewingTestResult(null)} className="close-button">&times;</button>
                            </div>
                        </header>
                        <div style={{ padding: '20px', backgroundColor: '#fff', color: '#000' }} className="printable-content">
                            {/* Header Info */}
                            <div style={{ borderBottom: '2px solid #2c3e50', paddingBottom: '15px', marginBottom: '20px' }}>
                                <h2 style={{ textAlign: 'center', margin: '0 0 15px 0' }}>{viewingTestResult.test_name}</h2>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                                    <div>
                                        <p style={{ margin: '5px 0' }}><strong>Pasiyent:</strong> {formatText(patient.ad)}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Doğum Tarixi:</strong> {patient.yas ? new Date(patient.yas).toLocaleDateString('az-AZ') : '-'}</p>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <p style={{ margin: '5px 0' }}><strong>Test Tarixi:</strong> {new Date(viewingTestResult.created_at).toLocaleDateString('az-AZ')}</p>
                                        <p style={{ margin: '5px 0' }}><strong>Mütəxəssis:</strong> {viewingTestResult.teacher_name || 'Bilinmir'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Scores Info */}
                            <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {(() => {
                                    let details = {};
                                    try { details = typeof viewingTestResult.details === 'string' ? JSON.parse(viewingTestResult.details) : (viewingTestResult.details || {}); } catch(e){}
                                    
                                    if (viewingTestResult.test_name && viewingTestResult.test_name.includes('AGTE')) {
                                        return (
                                            <>
                                                <InfoBox label="Dil-Bilişsel" raw={details.raw4} std={`${details.age4} ay`} />
                                                <InfoBox label="İncə Motor" raw={details.raw5} std={`${details.age5} ay`} />
                                                <InfoBox label="Kobud Motor" raw={details.raw6} std={`${details.age6} ay`} />
                                                <InfoBox label="Sosial" raw={details.raw7} std={`${details.age7} ay`} />
                                                <InfoBox label="Ümumi" raw={viewingTestResult.total_raw_score} std={`${details.generalAge} ay`} />
                                            </>
                                        );
                                    } else {
                                        return (
                                            <>
                                                <InfoBox label="Stereotip Davranış" raw={details.raw1} std={details.std1} />
                                                {details.childSpeaks !== false && (
                                                    <InfoBox label="Ünsiyyət" raw={details.raw2} std={details.std2} />
                                                )}
                                                <InfoBox label="Sosial Əlaqə" raw={details.raw3} std={details.std3} />
                                                <InfoBox label="OBİ Riski" raw={viewingTestResult.total_standard_score} std={viewingTestResult.index_score} />
                                            </>
                                        );
                                    }
                                })()}
                            </div>

                            {/* Answers Table */}
                            {(() => {
                                const templateQuestions = viewingTestResult.template?.questions || [];
                                const answerMap = {};
                                viewingTestResult.answers.forEach(a => { answerMap[a.question_id] = a.score; });

                                let details = {};
                                try { details = typeof viewingTestResult.details === 'string' ? JSON.parse(viewingTestResult.details) : (viewingTestResult.details || {}); } catch(e){}

                                if (viewingTestResult.test_name && viewingTestResult.test_name.includes('AGTE')) {
                                    return (
                                        <div className="test-questions-wrapper">
                                            {AGTE_MONTH_BOUNDARIES.map((boundary) => {
                                                const prevBoundary = AGTE_MONTH_BOUNDARIES[AGTE_MONTH_BOUNDARIES.indexOf(boundary) - 1];
                                                const startIdx = prevBoundary ? prevBoundary.endIdx + 1 : 1;
                                                const monthQuestions = templateQuestions.filter(
                                                    q => q.question_order >= startIdx && q.question_order <= boundary.endIdx
                                                );

                                                if (monthQuestions.length === 0) return null;

                                                return (
                                                    <div key={boundary.label} style={{ marginBottom: '15px', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', background: '#fff', breakInside: 'avoid' }}>
                                                        <div style={{ padding: '10px 15px', background: '#f8fafc', fontWeight: 'bold', color: '#34495e', borderBottom: '1px solid #e0e0e0' }}>
                                                            {boundary.label} SONUNA QƏDƏR
                                                        </div>
                                                        <table className="agte-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                <tr style={{ fontSize: '12px', color: '#95a5a6', textAlign: 'center', backgroundColor: '#fdfdfd' }}>
                                                                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #f1f1f1' }}>Sual Mətni</th>
                                                                    <th style={{ width: '45px', borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #f1f1f1' }}>N.B</th>
                                                                    <th style={{ width: '45px', borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #f1f1f1' }}>K.M</th>
                                                                    <th style={{ width: '45px', borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #f1f1f1' }}>B.M</th>
                                                                    <th style={{ width: '45px', borderBottom: '1px solid #e0e0e0' }}>S.B</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {monthQuestions.map(q => {
                                                                    const sId = Number(q.section_id);
                                                                    const score = answerMap[q.id];
                                                                    const isChecked = score === 1;
                                                                    return (
                                                                        <tr key={q.id} style={{ borderBottom: '1px solid #f1f1f1' }}>
                                                                            <td style={{ padding: '8px 10px', fontSize: '13px', borderRight: '1px solid #f1f1f1', color: '#2c3e50' }}>
                                                                                <strong style={{ marginRight: '10px', color: '#bdc3c7' }}>{q.question_order}.</strong> 
                                                                                {q.question_text}
                                                                            </td>
                                                                            {[4, 5, 6, 7].map(colId => (
                                                                                <td key={colId} style={{ padding: 0, borderRight: '1px solid #f1f1f1', textAlign: 'center' }}>
                                                                                    {sId === colId && (
                                                                                        <div style={{
                                                                                            margin: '0 auto',
                                                                                            width: '24px', height: '24px',
                                                                                            backgroundColor: isChecked ? AGTE_SECTIONS[colId].color : '#f8fafc',
                                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                            color: isChecked ? '#fff' : 'transparent', 
                                                                                            fontWeight: 'bold', fontSize: '14px',
                                                                                            borderRadius: '4px',
                                                                                            border: isChecked ? 'none' : `1px dashed #cbd5e1`
                                                                                        }}>
                                                                                            ✔
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div className="questions-list" style={{ border: '1px solid #eee', borderRadius: '8px', backgroundColor: '#fff' }}>
                                            {templateQuestions.map((q, idx) => {
                                                // Uşaq danışmırsa, 2-ci bölmənin (Ünsiyyət) suallarını gizlət
                                                if (details.childSpeaks === false && Number(q.section_id) === 2) return null;
                                                const score = answerMap[q.id];
                                                if (score === undefined) return null;

                                                return (
                                                    <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 15px', borderBottom: '1px solid #f0f0f0', alignItems: 'center', flexWrap: 'wrap', gap: '15px', breakInside: 'avoid' }}>
                                                        <div style={{ flex: '1 1 100%', fontSize: '0.9rem', color: '#2c3e50' }}>
                                                            <strong style={{ color: '#94a3b8' }}>{q.question_order || idx + 1}.</strong> {q.question_text}
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'center' }}>
                                                            {[0, 1, 2, 3].map(v => (
                                                                <div key={v} style={{ 
                                                                    width: '32px', height: '32px', borderRadius: '50%', 
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    border: '1px solid #cbd5e1', 
                                                                    backgroundColor: score === v ? '#1abc9c' : '#fff', 
                                                                    color: score === v ? '#fff' : '#475569', 
                                                                    fontWeight: 'bold', fontSize: '0.9rem',
                                                                    boxShadow: score === v ? '0 2px 4px rgba(26, 188, 156, 0.3)' : 'none'
                                                                }}>
                                                                    {v}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* QEEG Analiz Loading Overlay */}
            {analyzing && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
                    <div style={{ border: '4px solid #f3f3f3', borderTop: '4px solid #1abc9c', borderRadius: '50%', width: '50px', height: '50px', animation: 'spin 1s linear infinite' }}></div>
                    <p style={{ marginTop: '15px', fontWeight: 'bold', color: '#2c3e50', fontSize: '1.2rem' }}>QEEG Analiz edilir, zəhmət olmasa gözləyin...</p>
                </div>
            )}

            {/* Bildiriş Mesaj Qutusu (MsgBox) */}
            {statusMsg && (
                <div style={{ 
                    position: 'fixed', top: '20px', right: '20px', padding: '15px 25px', 
                    borderRadius: '8px', zIndex: 3100, boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                    backgroundColor: statusMsg.type === 'success' ? '#def7ec' : '#fde8e8',
                    color: statusMsg.type === 'success' ? '#03543f' : '#9b1c1c',
                    border: `1px solid ${statusMsg.type === 'success' ? '#84e1bc' : '#f8b4b4'}`,
                    display: 'flex', alignItems: 'center', gap: '12px', minWidth: '300px'
                }}>
                    <span style={{ fontSize: '1.3rem' }}>{statusMsg.type === 'success' ? '✅' : '❌'}</span>
                    <span style={{ flexGrow: 1, fontWeight: '600' }}>{statusMsg.text}</span>
                    <button onClick={() => setStatusMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'inherit', fontWeight: 'bold' }}>&times;</button>
                </div>
            )}

            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .printable-content, .printable-content * {
                        visibility: visible;
                    }
                    .printable-content {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        margin: 0;
                        padding: 0;
                    }
                    .patient-detail-modal-overlay {
                        position: absolute;
                        background: transparent !important;
                        box-shadow: none !important;
                    }
                    .patient-detail-modal-content {
                        box-shadow: none !important;
                        border: none !important;
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .patient-detail-header, .close-button, .action-button {
                        display: none !important;
                    }
                }

                .programs-section {
                    border-right: 1px solid #e2e8f0;
                }

                /* Dəyərləndirmə (Konsultasiya) Formunun Mobil Uyğunluğu */
                @media screen and (max-width: 768px) {
                    .consultation-table-container .doc-table,
                    .consultation-table-container .doc-table tbody,
                    .consultation-table-container .doc-table tr,
                    .consultation-table-container .doc-table th,
                    .consultation-table-container .doc-table td {
                        display: block;
                        width: 100%;
                        box-sizing: border-box;
                    }
                    .consultation-table-container .doc-table {
                        border: none;
                    }
                    .consultation-table-container .doc-table > tbody > tr {
                        margin-bottom: 15px;
                        border: 1px solid #e2e8f0;
                        border-radius: 8px;
                        background: #fff;
                    }
                    .consultation-table-container .doc-table > tbody > tr > th {
                        border: none;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .consultation-table-container .label-cell {
                        background-color: #f8fafc;
                        border-bottom: 1px solid #e2e8f0;
                        padding: 12px !important;
                        font-weight: 600;
                        color: #334155;
                    }
                    .consultation-table-container .input-cell {
                        padding: 15px !important;
                        border: none !important;
                    }
                    .consultation-table-container .table-input,
                    .consultation-table-container .table-textarea {
                        width: 100%;
                        box-sizing: border-box;
                    }
                    .checkbox-group-grid {
                        grid-template-columns: 1fr !important;
                    }
                    .programs-section {
                        border-right: none;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .radio-group-horizontal {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 10px;
                    }
                }
            `}</style>
        </div>
    );
};

export default PatientDetail;
